import { Link } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import {
	ArrowRight,
	Bot,
	ClipboardCheck,
	Database,
	FileText,
	HelpCircle,
	PlayCircle,
	ShieldAlert,
	Trash2,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";

import { EvalSampleProjection } from "@/components/eval-shared/eval-sample-projection";
import {
	type RunDetailField,
	type RunDetailStage,
	RunDetailStatusPill,
} from "@/components/run-detail/run-detail-workspace";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import {
	type PipelineAdminRunEvent,
	type PipelineAdminRunResponse,
	type PipelineDispatchResponse,
	type PipelineIntentResponse,
	type PipelineMessageResponse,
	type PipelinePhaseTraceEntry,
	type PipelineRuntimeCapabilities,
	runPipelineAdminRun,
	runPipelineAdminRunStream,
	runPipelineDispatch,
	runPipelineIntent,
	runPipelineKnowledgeSandbox,
	runPipelineMessage,
	runPipelineOAuthHealth,
	runPipelineSkillSandbox,
} from "@/lib/api/pipeline";
import {
	type AuditProjectionStatus,
	type RecallInjectionAuditItem,
	type RecallInjectionAuditProjection,
	buildRecallInjectionAuditProjection,
} from "@/lib/pipeline-audit-projection";
import {
	eventLabel,
	intentRuleLabel,
	knowledgeSourceLabel,
	finalActionLabel as productFinalActionLabel,
	intentLabel as productIntentLabel,
	playbookLabel as productPlaybookLabel,
	qualityDecisionLabel,
	serviceStageLabel,
	skillLabel,
	skillListLabel,
	targetStageLabel,
} from "@/lib/product-labels";

type DataMode = "mock" | "sandbox" | "real_read" | "real_write";
type StepStatus = "通过" | "运行中" | "受限" | "未配置" | "跳过";
type StepTone = "success" | "warning" | "neutral" | "blocked";
type RunStatus = "idle" | "running" | "success" | "error";
type RunEventSource = "pending" | "admin_run" | "admin_run_stream" | "messages_fallback";
type StageKind = "intent" | "playbook" | "tools" | "knowledge" | "prompt" | "quality";
type ContextRole = "user" | "assistant";
type ContextMessage = {
	id: string;
	role: ContextRole;
	content: string;
};
type RunFlowStageId =
	| "input_context"
	| "understanding_plan"
	| "evidence"
	| "reply_quality"
	| "audit";

type PipelineStep = {
	order: string;
	title: string;
	status: StepStatus;
	tone: StepTone;
	businessMeaning: string;
	input: string;
	output: string;
	nextAction: string;
	link?: {
		label: string;
		to: "/debug" | "/intent" | "/playbook" | "/skill-tool" | "/knowledge" | "/prompt-q" | "/eval";
	};
};

type StageResult = {
	kind: StageKind;
	title: string;
	status: "success" | "partial" | "error";
	summary: Array<{ label: string; value: string }>;
	raw: unknown;
};

type BusinessDecision = {
	semanticTask: string;
	decisionBasis: string;
	decisionBasisLabel: string;
	customerVisibleAction: string;
	evidenceStatus: string;
	evidenceStatusLabel: string;
	missingSlots: string[];
	verificationScope: string;
	nextAction: string;
	tone: StepTone;
};

type ResponseEffectItem = {
	label: string;
	value: string;
	summary: string;
	tone: StepTone;
};

type RuntimeCapabilityItem = {
	label: string;
	value: string;
	tone: StepTone;
};

type RuntimeDiagnosticItem = {
	label: string;
	value: string;
};

type RunFlowStage = RunDetailStage<RunFlowStageId>;

type EvalReplayContext = {
	source: "eval" | "p10_tool_audit";
	sampleId: string;
	expectedIntent: string | null;
	actualIntent: string | null;
	expectedPb: string | null;
	actualPb: string | null;
	rootCauseHint: string | null;
	responsibleAsset: string | null;
	badcaseReason: string | null;
	evalRunId: string | null;
	sampleResultId: string | null;
	badcaseId: string | null;
};

type ContinuityContext = {
	previousIntent: string | null;
	currentPlaybookId: string | null;
};

type RunFlowBuildContext = {
	events: PipelineAdminRunEvent[];
	allEvents: PipelineAdminRunEvent[];
	eventSource: RunEventSource;
	runStatus: RunStatus;
	evalContext: EvalReplayContext | null;
};

const MERCHANT_ID = "mer_nY0bZmLI";

const DATA_MODES: Array<{
	id: DataMode;
	label: string;
	description: string;
	guidance: string;
	useCase: string;
	proves: string;
	doesNotProve: string;
	state: string;
}> = [
	{
		id: "mock",
		label: "Mock",
		description: "用于前端和规则演示，mock 结果不能当作真实店铺验证。",
		guidance: "离线演示：只证明页面交互和 fixture 回归，不作为产品验收证据。",
		useCase: "无后端、演示页面动效、验证前端字段映射和固定样例回归。",
		proves: "页面能跑、字段能展示、fixture 覆盖了这类问法。",
		doesNotProve: "真实 Shopify、Qdrant、权限、主 Agent 线上效果已经跑通。",
		state: "演示",
	},
	{
		id: "sandbox",
		label: "沙盒",
		description: "调用沙盒能力或保护性试跑，验证工具入参和权限保护。",
		guidance: "工具自检：适合看入参、权限检查和保护性试跑，不代表真实订单被改动。",
		useCase: "调试工具、提示词沙盒、知识检索预览、工具入参和写操作保护性试跑。",
		proves: "参数结构、权限判断、保护策略和沙盒路径基本可用。",
		doesNotProve: "动作已经写入真实店铺，或真实客户会看到同样结果。",
		state: "可测",
	},
	{
		id: "real_read",
		label: "真实只读",
		description: "只读真实店铺事实，不执行会改变订单或客户数据的动作。",
		guidance: "日常验收推荐：读取真实商品、店铺和知识事实，但不触碰真实写操作。",
		useCase: "产品/运营验收回答质量、商品事实、政策知识和多轮上下文影响。",
		proves: "线上主链路能读取真实外部事实并组装回复。",
		doesNotProve: "取消、退款、改地址等写动作已经真实执行。",
		state: "受限",
	},
	{
		id: "real_write",
		label: "真实写入",
		description: "需要人工确认和权限审批，当前写操作默认保护性试跑。",
		guidance: "保护写入：只在测试店铺、审计和人工确认齐备时验证真实动作。",
		useCase: "后续验证取消、退款、改地址、退货等会改变数据的受控动作。",
		proves: "在隔离测试对象上，写操作合同、审计和回滚路径可闭环。",
		doesNotProve: "可以直接推广到生产商户或跳过人工确认。",
		state: "保护",
	},
];

const DEPENDENCY_ROWS = [
	{
		name: "Shopify 商品/店铺",
		state: "真实只读可用",
		detail: "可用于商品事实和店铺配置检查。",
		tone: "success" as const,
	},
	{
		name: "Shopify 订单/客户",
		state: "Shopify 订单权限未获批",
		detail: "订单、客户数据仍受 PCD 审批限制，不能伪装成真实已通。",
		tone: "warning" as const,
	},
	{
		name: "Qdrant 知识检索",
		state: "Qdrant runtime 未配置",
		detail: "当前只展示知识事实入口，真实 RAG 闭环留给 P5。",
		tone: "warning" as const,
	},
	{
		name: "写操作能力",
		state: "写操作默认保护性试跑",
		detail: "取消、退款、改地址等动作不会直接写入真实店铺。",
		tone: "neutral" as const,
	},
];

function usePrefersReducedMotion(): boolean {
	const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

	useEffect(() => {
		if (typeof window === "undefined" || !window.matchMedia) return;
		const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
		setPrefersReducedMotion(mediaQuery.matches);
		const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);
		mediaQuery.addEventListener("change", handleChange);
		return () => mediaQuery.removeEventListener("change", handleChange);
	}, []);

	return prefersReducedMotion;
}

function initialCustomerMessage(): string {
	const fallback = "我上周买的 snowboard 什么时候发货？如果还没发能不能改地址？";
	if (typeof window === "undefined") return fallback;
	const params = new URLSearchParams(window.location.search);
	const message = params.get("message");
	if (params.get("replay_context") === "1") {
		return message ?? "";
	}
	return message && message.trim().length > 0 ? message : fallback;
}

function initialContextMessages(): ContextMessage[] {
	if (typeof window === "undefined") return [];
	const history = new URLSearchParams(window.location.search).get("history");
	if (!history) return [];
	try {
		const parsed = JSON.parse(history) as unknown;
		if (!Array.isArray(parsed)) return [];
		return parsed
			.map((item) => normalizeContextMessage(item))
			.filter((item): item is ContextMessage => item !== null);
	} catch {
		return [];
	}
}

function initialSessionId(): string | null {
	if (typeof window === "undefined") return null;
	const sessionId = new URLSearchParams(window.location.search).get("session_id");
	return sessionId && sessionId.trim().length > 0 ? sessionId.trim() : null;
}

function initialContinuityContext(): ContinuityContext {
	if (typeof window === "undefined") {
		return { previousIntent: null, currentPlaybookId: null };
	}
	const params = new URLSearchParams(window.location.search);
	return {
		previousIntent:
			nonEmptyParam(params, "previous_intent") ??
			nonEmptyParam(params, "actual_intent") ??
			nonEmptyParam(params, "expected_intent"),
		currentPlaybookId:
			nonEmptyParam(params, "current_playbook_id") ??
			nonEmptyParam(params, "actual_pb") ??
			nonEmptyParam(params, "expected_pb"),
	};
}

function nonEmptyParam(params: URLSearchParams, key: string): string | null {
	const value = params.get(key)?.trim();
	return value ? value : null;
}

function initialDataMode(): DataMode {
	if (typeof window === "undefined") return "real_read";
	const params = new URLSearchParams(window.location.search);
	return params.get("mock") === "1" ? "mock" : "real_read";
}

function initialEvalReplayContext(): EvalReplayContext | null {
	if (typeof window === "undefined") return null;
	const params = new URLSearchParams(window.location.search);
	const source = params.get("source");
	if (source !== "eval" && source !== "p10_tool_audit" && !params.get("sample_id")) return null;
	const sampleId = params.get("sample_id")?.trim();
	if (!sampleId) return null;
	return {
		source: source === "p10_tool_audit" ? "p10_tool_audit" : "eval",
		sampleId,
		expectedIntent: params.get("expected_intent"),
		actualIntent: params.get("actual_intent"),
		expectedPb: params.get("expected_pb"),
		actualPb: params.get("actual_pb"),
		rootCauseHint: params.get("root_cause_hint"),
		responsibleAsset: params.get("responsible_asset"),
		badcaseReason: params.get("badcase_reason"),
		evalRunId: params.get("eval_run_id"),
		sampleResultId: params.get("sample_result_id"),
		badcaseId: params.get("badcase_id"),
	};
}

function normalizeContextMessage(item: unknown): ContextMessage | null {
	if (!item || typeof item !== "object") return null;
	const record = item as Record<string, unknown>;
	const role =
		record.role === "agent"
			? "assistant"
			: record.role === "user" || record.role === "assistant"
				? record.role
				: null;
	const content = typeof record.content === "string" ? record.content.trim() : "";
	if (!role || !content) return null;
	return createContextMessage(role, content);
}

function contextMessagesToApiHistory(
	messages: ContextMessage[],
): Array<{ role: "user" | "agent"; content: string }> {
	return messages.map(({ role, content }) => ({
		role: role === "assistant" ? "agent" : "user",
		content,
	}));
}

function stageWithContext(
	result: StageResult,
	history: Array<{ role: "user" | "agent"; content: string }>,
): StageResult {
	return {
		...result,
		summary: [{ label: "带入上文", value: `${history.length} 条` }, ...result.summary],
	};
}

function contextualSandboxMessage(message: string, contextMessages: ContextMessage[]): string {
	const trimmed = message.trim();
	if (contextMessages.length === 0) return trimmed;
	const recent = contextMessages
		.slice(-2)
		.map((item) => `${item.role === "assistant" ? "助手" : "用户"}：${item.content}`)
		.join("；");
	return `${trimmed}（上文：${recent}）`;
}

function upsertRunEvent(
	events: PipelineAdminRunEvent[],
	nextEvent: PipelineAdminRunEvent,
): PipelineAdminRunEvent[] {
	const index = events.findIndex((item) => item.event_id === nextEvent.event_id);
	if (index === -1) return [...events, nextEvent];
	return events.map((item, itemIndex) => (itemIndex === index ? nextEvent : item));
}

export function PipelineTestbenchShell() {
	const prefersReducedMotion = usePrefersReducedMotion();
	const [evalReplayContext] = useState<EvalReplayContext | null>(() => initialEvalReplayContext());
	const [mode, setMode] = useState<DataMode>(() => initialDataMode());
	const [customerMessage, setCustomerMessage] = useState(() => initialCustomerMessage());
	const [contextMessages, setContextMessages] = useState<ContextMessage[]>(() =>
		initialContextMessages(),
	);
	const [activeSessionId, setActiveSessionId] = useState<string | null>(() => initialSessionId());
	const [continuityContext, setContinuityContext] = useState<ContinuityContext>(() =>
		initialContinuityContext(),
	);
	const [fullRunStatus, setFullRunStatus] = useState<RunStatus>("idle");
	const [fullRunResult, setFullRunResult] = useState<PipelineMessageResponse | null>(null);
	const [fullRunError, setFullRunError] = useState<string | null>(null);
	const [stageRunning, setStageRunning] = useState<StageKind | null>(null);
	const [stageResult, setStageResult] = useState<StageResult | null>(null);
	const [stageError, setStageError] = useState<string | null>(null);
	const [activeRunStage, setActiveRunStage] = useState<RunFlowStageId>("input_context");
	const [lastSubmittedHistoryCount, setLastSubmittedHistoryCount] = useState<number | null>(null);
	const [lastRunCustomerMessage, setLastRunCustomerMessage] = useState<string | null>(null);
	const [lastAdminRunTotalLatencyMs, setLastAdminRunTotalLatencyMs] = useState<number | null>(null);
	const [runEvents, setRunEvents] = useState<PipelineAdminRunEvent[]>([]);
	const [visibleRunEventCount, setVisibleRunEventCount] = useState(0);
	const [runEventSource, setRunEventSource] = useState<RunEventSource>("pending");
	const visibleRunEvents = useMemo(
		() => runEvents.slice(0, visibleRunEventCount),
		[runEvents, visibleRunEventCount],
	);
	const activePipelineMessage = lastRunCustomerMessage ?? customerMessage;
	const steps = useMemo(
		() => buildPipelineSteps(mode, activePipelineMessage, fullRunResult),
		[mode, activePipelineMessage, fullRunResult],
	);
	const runFlowStages = useMemo(
		() =>
			buildRunFlowStages(
				mode,
				activePipelineMessage,
				contextMessages.length,
				lastSubmittedHistoryCount,
				fullRunResult,
				steps,
				{
					events: visibleRunEvents,
					allEvents: runEvents,
					eventSource: runEventSource,
					runStatus: fullRunStatus,
					evalContext: evalReplayContext,
				},
			),
		[
			mode,
			activePipelineMessage,
			contextMessages.length,
			lastSubmittedHistoryCount,
			fullRunResult,
			steps,
			visibleRunEvents,
			runEvents,
			runEventSource,
			fullRunStatus,
			evalReplayContext,
		],
	);
	const selectedMode = DATA_MODES.find((item) => item.id === mode);

	useEffect(() => {
		if (runEvents.length === 0) {
			setVisibleRunEventCount(0);
			return;
		}
		if (prefersReducedMotion) {
			setVisibleRunEventCount(runEvents.length);
			return;
		}
		if (visibleRunEventCount >= runEvents.length) return;
		const timer = window.setTimeout(() => {
			setVisibleRunEventCount((count) => Math.min(count + 1, runEvents.length));
		}, 90);
		return () => window.clearTimeout(timer);
	}, [prefersReducedMotion, runEvents.length, visibleRunEventCount]);

	function resetRunState(options?: { resetSession?: boolean }) {
		setFullRunStatus("idle");
		setFullRunResult(null);
		setFullRunError(null);
		setStageResult(null);
		setStageError(null);
		setLastSubmittedHistoryCount(null);
		setLastRunCustomerMessage(null);
		setLastAdminRunTotalLatencyMs(null);
		setRunEvents([]);
		setVisibleRunEventCount(0);
		setRunEventSource("pending");
		if (options?.resetSession) {
			setActiveSessionId(null);
			setContinuityContext({ previousIntent: null, currentPlaybookId: null });
		}
	}

	function clearConversation() {
		setContextMessages([]);
		setActiveSessionId(null);
		setCustomerMessage("");
		resetRunState({ resetSession: true });
	}

	async function handleFullRun() {
		const submittedMessage = customerMessage.trim();
		if (!submittedMessage) return;
		const submittedHistory = contextMessagesToApiHistory(contextMessages);
		setFullRunStatus("running");
		setFullRunError(null);
		setStageResult(null);
		setStageError(null);
		setFullRunResult(null);
		setLastRunCustomerMessage(submittedMessage);
		setLastSubmittedHistoryCount(submittedHistory.length);
		setRunEvents([]);
		setVisibleRunEventCount(0);
		setRunEventSource("pending");
		try {
			const continuityPayload = {
				previous_intent: continuityContext.previousIntent,
				current_playbook_id: continuityContext.currentPlaybookId,
			};
			const adminRun =
				mode === "mock"
					? await runPipelineAdminRun({
							merchant_id: MERCHANT_ID,
							session_id: activeSessionId,
							message: submittedMessage,
							history: submittedHistory,
							...continuityPayload,
							data_mode: mode,
						})
					: await runPipelineAdminRunStream(
							{
								merchant_id: MERCHANT_ID,
								session_id: activeSessionId,
								message: submittedMessage,
								history: submittedHistory,
								...continuityPayload,
								data_mode: mode,
							},
							{
								onEvent: (event) => {
									setRunEventSource("admin_run_stream");
									setRunEvents((items) => upsertRunEvent(items, event));
								},
							},
						);
			const result = adminRun.messages_response;
			setLastAdminRunTotalLatencyMs(
				adminRun.total_latency_ms ?? totalLatencyFromEvents(adminRun.events),
			);
			setRunEvents(adminRun.events);
			setVisibleRunEventCount(mode === "mock" ? 0 : adminRun.events.length);
			setRunEventSource(mode === "mock" ? "admin_run" : "admin_run_stream");
			setFullRunResult(result);
			setActiveSessionId(result.session_id);
			rememberContinuityFromResult(result);
			setContextMessages((items) => [
				...items,
				createContextMessage("user", submittedMessage),
				createContextMessage("assistant", result.reply_text.trim()),
			]);
			setCustomerMessage("");
			setFullRunStatus("success");
		} catch (error) {
			try {
				const result = await runPipelineMessage({
					merchant_id: MERCHANT_ID,
					session_id: activeSessionId,
					message: submittedMessage,
					history: submittedHistory,
					previous_intent: continuityContext.previousIntent,
					current_playbook_id: continuityContext.currentPlaybookId,
				});
				setRunEvents([]);
				setVisibleRunEventCount(0);
				setRunEventSource("messages_fallback");
				setLastAdminRunTotalLatencyMs(null);
				setFullRunResult(result);
				setActiveSessionId(result.session_id);
				rememberContinuityFromResult(result);
				setContextMessages((items) => [
					...items,
					createContextMessage("user", submittedMessage),
					createContextMessage("assistant", result.reply_text.trim()),
				]);
				setCustomerMessage("");
				setFullRunStatus("success");
			} catch (fallbackError) {
				setFullRunResult(null);
				setFullRunError(`${errorMessage(error)}；回退失败：${errorMessage(fallbackError)}`);
				setFullRunStatus("error");
			}
		}
	}

	function rememberContinuityFromResult(result: PipelineMessageResponse) {
		setContinuityContext((current) => ({
			previousIntent: result.intent.intent || current.previousIntent,
			currentPlaybookId:
				result.recommended_playbook?.id ??
				(result.match_mode === "skipped" ? current.currentPlaybookId : null),
		}));
	}

	async function handleIntentRun() {
		const history = contextMessagesToApiHistory(contextMessages);
		await runStage("intent", async () =>
			stageWithContext(
				stageFromIntent(
					await runPipelineIntent({
						message: customerMessage,
						history,
						previous_intent: continuityContext.previousIntent,
					}),
				),
				history,
			),
		);
	}

	async function handlePlaybookRun() {
		const history = contextMessagesToApiHistory(contextMessages);
		await runStage("playbook", async () =>
			stageWithContext(
				stageFromDispatch(
					await runPipelineDispatch({
						merchant_id: MERCHANT_ID,
						message: customerMessage,
						history,
						previous_intent: continuityContext.previousIntent,
						current_playbook_id: continuityContext.currentPlaybookId,
					}),
				),
				history,
			),
		);
	}

	async function handleToolRun() {
		const contextualMessage = contextualSandboxMessage(customerMessage, contextMessages);
		await runStage("tools", async () => {
			const [oauth, skill, knowledge] = await Promise.allSettled([
				runPipelineOAuthHealth(MERCHANT_ID),
				runPipelineSkillSandbox(MERCHANT_ID, contextualMessage),
				runPipelineKnowledgeSandbox(MERCHANT_ID, contextualMessage),
			]);
			return stageFromTools(oauth, skill, knowledge);
		});
	}

	async function handleKnowledgeRun() {
		const contextualMessage = contextualSandboxMessage(customerMessage, contextMessages);
		await runStage("knowledge", async () =>
			stageFromKnowledge(await runPipelineKnowledgeSandbox(MERCHANT_ID, contextualMessage)),
		);
	}

	async function handlePromptRun() {
		const history = contextMessagesToApiHistory(contextMessages);
		await runStage("prompt", async () =>
			stageFromPromptResponse(
				await runPipelineAdminRun({
					merchant_id: MERCHANT_ID,
					session_id: activeSessionId,
					message: customerMessage,
					history,
					previous_intent: continuityContext.previousIntent,
					current_playbook_id: continuityContext.currentPlaybookId,
					data_mode: mode,
				}),
			),
		);
	}

	async function handleQualityRun() {
		const history = contextMessagesToApiHistory(contextMessages);
		await runStage("quality", async () =>
			stageFromQualityGuard(
				await runPipelineAdminRun({
					merchant_id: MERCHANT_ID,
					session_id: activeSessionId,
					message: customerMessage,
					history,
					previous_intent: continuityContext.previousIntent,
					current_playbook_id: continuityContext.currentPlaybookId,
					data_mode: mode,
				}),
			),
		);
	}

	async function runStage(kind: StageKind, task: () => Promise<StageResult>) {
		setStageRunning(kind);
		setStageError(null);
		try {
			setStageResult(await task());
		} catch (error) {
			setStageResult(null);
			setStageError(errorMessage(error));
		} finally {
			setStageRunning(null);
		}
	}

	return (
		<div className="space-y-5" data-testid="pipeline-testbench">
			<TraceChatConsole
				mode={mode}
				selectedModeLabel={selectedMode?.label ?? "Mock"}
				customerMessage={customerMessage}
				contextMessages={contextMessages}
				status={fullRunStatus}
				error={fullRunError}
				result={fullRunResult}
				lastRunCustomerMessage={lastRunCustomerMessage}
				stages={runFlowStages}
				runEvents={visibleRunEvents}
				allRunEventCount={runEvents.length}
				totalLatencyMs={lastAdminRunTotalLatencyMs ?? totalLatencyFromEvents(runEvents)}
				runEventSource={runEventSource}
				evalContext={evalReplayContext}
				activeStageId={activeRunStage}
				onSelectStage={setActiveRunStage}
				stageRunning={stageRunning}
				onModeChange={(nextMode) => {
					setMode(nextMode);
					resetRunState({ resetSession: true });
				}}
				onCustomerMessageChange={(message) => {
					setCustomerMessage(message);
					resetRunState();
				}}
				onClearConversation={clearConversation}
				onFullRun={handleFullRun}
				onIntentRun={handleIntentRun}
				onPlaybookRun={handlePlaybookRun}
				onToolRun={handleToolRun}
				onKnowledgeRun={handleKnowledgeRun}
				onPromptRun={handlePromptRun}
				onQualityRun={handleQualityRun}
			/>

			<details className="rounded-md border border-surface-border bg-surface-card">
				<summary className="flex cursor-pointer list-none flex-col gap-2 border-b border-surface-border px-4 py-3 md:flex-row md:items-center md:justify-between">
					<div>
						<h3 className="text-base font-semibold text-text-primary">字段级排障表</h3>
						<p className="mt-1 text-sm text-text-secondary">
							需要逐项核对时展开。这里保留完整节点、输入输出和维护入口，不再作为默认主视图。
						</p>
					</div>
					<div className="text-xs text-text-muted">展开查看 16 个节点和审计</div>
				</summary>
				<div className="divide-y divide-surface-border lg:hidden">
					{steps.map((step) => (
						<article key={step.order} className="space-y-3 px-4 py-4">
							<div className="flex items-start justify-between gap-3">
								<div>
									<h4 className="text-sm font-semibold text-text-primary">
										{step.order} {step.title}
									</h4>
									<p className="mt-1 text-xs text-text-muted">{step.nextAction}</p>
								</div>
								<StatusPill tone={step.tone}>{step.status}</StatusPill>
							</div>
							<p className="text-sm leading-6 text-text-secondary">{step.businessMeaning}</p>
							<div className="space-y-1 rounded-md bg-surface-subtle p-2 font-mono text-xs leading-5 text-text-secondary">
								<div>in: {step.input}</div>
								<div>out: {step.output}</div>
							</div>
							{step.link ? (
								<Link
									to={step.link.to}
									className="inline-flex items-center gap-1 text-sm font-medium text-brand-primary hover:underline"
								>
									{step.link.label}
									<ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
								</Link>
							) : (
								<span className="text-xs text-text-muted">本页</span>
							)}
						</article>
					))}
				</div>
				<div className="hidden overflow-x-auto lg:block">
					<table className="w-full min-w-[980px] border-collapse text-sm">
						<thead className="bg-surface-subtle text-left text-xs text-text-muted">
							<tr>
								<th className="w-[230px] px-4 py-3 font-medium">节点</th>
								<th className="w-[90px] px-3 py-3 font-medium">状态</th>
								<th className="px-3 py-3 font-medium">业务解释</th>
								<th className="px-3 py-3 font-medium">输入/输出</th>
								<th className="w-[120px] px-3 py-3 font-medium">维护入口</th>
							</tr>
						</thead>
						<tbody>
							{steps.map((step) => (
								<tr key={step.order} className="border-t border-surface-border align-top">
									<td className="px-4 py-3">
										<div className="font-medium text-text-primary">
											{step.order} {step.title}
										</div>
										<div className="mt-1 text-xs text-text-muted">{step.nextAction}</div>
									</td>
									<td className="px-3 py-3">
										<StatusPill tone={step.tone}>{step.status}</StatusPill>
									</td>
									<td className="max-w-sm px-3 py-3 text-text-secondary">{step.businessMeaning}</td>
									<td className="px-3 py-3">
										<div className="space-y-1 rounded-md bg-surface-subtle p-2 font-mono text-xs leading-5 text-text-secondary">
											<div>in: {step.input}</div>
											<div>out: {step.output}</div>
										</div>
									</td>
									<td className="px-3 py-3">
										{step.link ? (
											<Link
												to={step.link.to}
												className="inline-flex items-center gap-1 text-sm font-medium text-brand-primary hover:underline"
											>
												{step.link.label}
												<ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
											</Link>
										) : (
											<span className="text-xs text-text-muted">本页</span>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</details>

			<StageRunResult mode={mode} result={stageResult} error={stageError} running={stageRunning} />

			{fullRunStatus === "idle" ? (
				<EmptyRunState />
			) : (
				<RunConclusion
					mode={mode}
					result={fullRunResult}
					status={fullRunStatus}
					error={fullRunError}
					runEvents={runEvents}
					totalLatencyMs={lastAdminRunTotalLatencyMs ?? totalLatencyFromEvents(runEvents)}
				/>
			)}
		</div>
	);
}

function buildPipelineSteps(
	mode: DataMode,
	customerMessage: string,
	result: PipelineMessageResponse | null,
): PipelineStep[] {
	const modeLabel = DATA_MODES.find((item) => item.id === mode)?.label ?? "Mock";
	const phaseByName = new Map(result?.phase_trace.map((entry) => [entry.phase, entry]) ?? []);
	const phase1 = phaseByName.get("phase1_refine");
	const phase2 = phaseByName.get("phase2_recall");
	const phase3 = phaseByName.get("phase3_rerank");
	const phase4 = phaseByName.get("phase4_generate");
	const phase5 = phaseByName.get("phase5_validate");
	const semanticFrame = asRecord(phase2?.summary.semantic_frame);
	const skillResultCount = summaryNumber(phase2?.summary.skill_result_count);
	const skillDecisionPlan = skillDecisionPlanLabel(phase2?.summary.skill_decisions);
	const ragSnippetCount = summaryNumber(phase2?.summary.rag_snippet_count);
	const phase4Action = summaryText(phase4?.summary, "action_type", "");
	const phase4DecisionBasis = summaryText(phase4?.summary, "decision_basis", "");
	const finalStepState = finalStepStatus(result);
	const phase4StepState = phase4
		? phase4Action === "escalate" || result?.final_action_type === "handoff"
			? ({ status: "受限", tone: "blocked" } as const)
			: phase4Action === "clarify" || phase4DecisionBasis === "missing_required_information"
				? ({ status: "受限", tone: "warning" } as const)
				: ({ status: "通过", tone: "success" } as const)
		: ({ status: "跳过", tone: "neutral" } as const);
	const routingTarget = result ? routingTargetLabel(result) : "等待运行后展示";
	const runtimeBranch = runtimeBranchLabel(result, phase2);
	const actualAllowedSkills = phaseAllowedSkillsLabel(phase2);
	const recommendedSkills =
		result === null
			? "等待运行后展示"
			: result.recommended_skills.length > 0
				? result.recommended_skills.join(",")
				: "无";
	const universalSkills =
		result === null
			? "等待运行后展示"
			: result.universal_skills.length > 0
				? result.universal_skills.join(",")
				: "无";
	const readStatus: Pick<PipelineStep, "status" | "tone" | "output" | "nextAction"> = phase2
		? skillResultCount && skillResultCount > 0
			? {
					status: "通过",
					tone: "success",
					output: `本次返回 ${skillResultCount} 条工具事实`,
					nextAction: "继续检查事实是否足够生成回复",
				}
			: {
					status: "跳过",
					tone: "neutral",
					output: "本次没有返回工具事实",
					nextAction: "若业务问题需要事实，回到工具与动作检查入参与权限",
				}
		: mode === "real_read"
			? {
					status: "受限",
					tone: "warning",
					output: "商品事实可读；Shopify 订单权限未获批",
					nextAction: "等待 PCD 后补真实订单样本",
				}
			: mode === "real_write"
				? {
						status: "受限",
						tone: "blocked",
						output: "写操作默认保护性试跑，真实写入未开放",
						nextAction: "只允许人工确认后的保护执行",
					}
				: {
						status: "通过",
						tone: "success",
						output: `${modeLabel} 事实返回：商品存在，订单线索待确认`,
						nextAction: "继续检查事实是否足够生成回复",
					};

	const knowledgeStatus: Pick<PipelineStep, "status" | "tone" | "output" | "nextAction"> = phase2
		? ragSnippetCount && ragSnippetCount > 0
			? {
					status: "通过",
					tone: "success",
					output: `本次返回 ${ragSnippetCount} 条知识片段`,
					nextAction: "继续检查引用是否进入回复",
				}
			: {
					status: "跳过",
					tone: "neutral",
					output: "本次没有返回知识片段",
					nextAction: "若业务问题需要政策，回到知识与证据检查数据集和命中词",
				}
		: mode === "mock" || mode === "sandbox"
			? {
					status: "通过",
					tone: "success",
					output: "命中 return policy 与 shipping policy 示例片段",
					nextAction: "P5 接入真实 Qdrant 后替换 mock 片段",
				}
			: {
					status: "未配置",
					tone: "warning",
					output: "Qdrant runtime 未配置，真实 RAG 暂不可用",
					nextAction: "接入 collection、token 和运行态健康检查",
				};

	return [
		{
			order: "01",
			title: "环境与商户状态",
			status: "通过",
			tone: "success",
			businessMeaning: "先确认当前商户、API 和外部依赖状态，避免把权限问题误判为产品逻辑失败。",
			input: "merchant=mer_nY0bZmLI",
			output: "Admin health ok；Shopify 商品只读可用",
			nextAction: "看首页边界和工具健康",
		},
		{
			order: "02",
			title: "数据模式选择",
			status: "通过",
			tone: "success",
			businessMeaning: "每次运行必须带数据模式，避免 mock、沙盒和真实数据混在一起看。",
			input: "mode",
			output: modeLabel,
			nextAction: "确认本次证据可用于哪类验收",
		},
		{
			order: "03",
			title: "测试样本/数据准备",
			status: "通过",
			tone: "success",
			businessMeaning: "把样本来源和期望结果放在链路前面，后续 P3 可直接沉淀为测试集。",
			input: result ? "本次自定义输入" : "默认样本/自定义输入",
			output: result ? "已按本次输入生成运行证据" : "运行后展示本次样本与期望差异",
			nextAction: "失败样本可回写测试集",
			link: { label: "评测运营", to: "/eval" },
		},
		{
			order: "04",
			title: "客户问题与会话上下文",
			status: "通过",
			tone: "success",
			businessMeaning: "保留客户原话和上下文，方便判断问题整理有没有改坏用户意图。",
			input: customerMessage,
			output: result ? `session=${result.session_id}` : "等待运行后展示 session",
			nextAction: "必要时回看原始对话",
			link: { label: "运行记录", to: "/debug" },
		},
		{
			order: "05",
			title: "问题整理",
			status: "通过",
			tone: "success",
			businessMeaning: "先做安全和槽位整理，再决定是否需要澄清，避免直接进入工具调用。",
			input: "客户原话 + history",
			output: phase1
				? `status=${phase1.status}；${summaryPreview(phase1.summary)}`
				: "等待运行后展示安全检查与槽位",
			nextAction: "缺订单号时进入澄清或只读查询",
			link: { label: "运行记录", to: "/debug" },
		},
		{
			order: "06",
			title: "意图识别",
			status: "通过",
			tone: "success",
			businessMeaning: "判断客户问题属于哪一类，给方案匹配提供稳定入口。",
			input: "整理后的问题",
			output: result
				? `${result.intent.intent}；置信度 ${formatPercent(result.intent.confidence)}；${result.intent.implementation_level}`
				: "等待运行后展示意图和置信度",
			nextAction: "分类不对时回到意图分类",
			link: { label: "意图分类", to: "/intent" },
		},
		{
			order: "06.5",
			title: "结构化语义理解",
			status: phase2 ? "通过" : "跳过",
			tone: phase2 ? "success" : "neutral",
			businessMeaning:
				"把客户目标、偏好、约束、缺口和证据需求拆开，方案、工具、知识和回复都参考这份底稿，但最终决策仍由主 Agent 完成。",
			input: result ? `intent=${result.intent.intent}` : "等待运行后展示意图与整理后问题",
			output: semanticFrame
				? `任务=${stringifyScalar(semanticFrame.semantic_task)}；证据=${stringifyScalar(semanticFrame.evidence_needs)}；缺口=${stringifyScalar(semanticFrame.missing_slots)}`
				: "等待理解与证据阶段返回结构化语义",
			nextAction: "语义任务不对时回到意图分类和处理方案联查",
			link: { label: "意图分类", to: "/intent" },
		},
		{
			order: "07",
			title: "处理路径匹配",
			status: "通过",
			tone: "success",
			businessMeaning: "展示为什么选中当前方案，以及为什么没有选其他处理路径。",
			input: result ? `intent=${result.intent.intent}` : "等待运行后展示 intent",
			output: result
				? `命中路径：${routingTarget}；分支=${runtimeBranch}；match_mode=${result.match_mode}`
				: "等待运行后展示处理路径匹配",
			nextAction: "方案不对时回到处理方案",
			link: { label: "处理方案", to: "/playbook" },
		},
		{
			order: "08",
			title: "工具计划与权限检查",
			status: mode === "real_write" ? "受限" : "通过",
			tone: mode === "real_write" ? "blocked" : "success",
			businessMeaning: "先判断需要哪些工具、是否允许预取、是否需要人工确认，防止误执行写操作。",
			input: `allowed_skills=${actualAllowedSkills}`,
			output:
				mode === "real_write"
					? "写操作默认保护性试跑，真实写入需人工确认"
					: result
						? `方案推荐=${recommendedSkills}；通用=${universalSkills}；Skill判断=${skillDecisionPlan}`
						: "等待运行后展示工具计划",
			nextAction: "权限或预算异常时回到工具与动作",
			link: { label: "工具与动作", to: "/skill-tool" },
		},
		{
			order: "09",
			title: "工具事实获取",
			status: readStatus.status,
			tone: readStatus.tone,
			businessMeaning: "把 Shopify 等事实单独展示，区分真实事实、mock 事实、空事实和权限阻塞。",
			input: skillDecisionPlan === "未返回" ? "Shopify product/order lookup" : skillDecisionPlan,
			output: phase2
				? `skill_result_count=${stringifyScalar(phase2.summary.skill_result_count)}；${readStatus.output}`
				: readStatus.output,
			nextAction: readStatus.nextAction,
			link: { label: "工具与动作", to: "/skill-tool" },
		},
		{
			order: "10",
			title: "知识事实获取",
			status: knowledgeStatus.status,
			tone: knowledgeStatus.tone,
			businessMeaning: "把政策、FAQ 等知识证据与工具事实分开，避免回复引用来源不清。",
			input: "shipping policy + address policy",
			output: phase2
				? `rag_snippet_count=${stringifyScalar(phase2.summary.rag_snippet_count)}；${knowledgeStatus.output}`
				: knowledgeStatus.output,
			nextAction: knowledgeStatus.nextAction,
			link: { label: "知识与证据", to: "/knowledge" },
		},
		{
			order: "11",
			title: "证据整理/重排",
			status: "通过",
			tone: "success",
			businessMeaning: "说明哪些证据进入回复，哪些被降权或丢弃，避免最终答案像黑盒。",
			input: "工具事实 + 知识事实 + 方案要求",
			output: phase3
				? `status=${phase3.status}；${summaryPreview(phase3.summary)}`
				: "等待运行后展示证据保留与丢弃情况",
			nextAction: "证据不充分时要求澄清",
		},
		{
			order: "12",
			title: "回复组装",
			status: phase4StepState.status,
			tone: phase4StepState.tone,
			businessMeaning: "展示提示词版本、证据区和本轮动作，让运营能判断话术是否符合预期。",
			input: "d' evidence_refs + playbook",
			output: phase4
				? `action_type=${stringifyScalar(phase4.summary.action_type)}；依据=${stringifyScalar(phase4.summary.decision_basis)}；evidence=${stringifyScalar(phase4.summary.evidence_refs)}`
				: "等待运行后展示回复动作和证据引用",
			nextAction: "话术不清楚时回到提示词与话术",
			link: { label: "提示词与话术", to: "/prompt-q" },
		},
		{
			order: "13",
			title: "质量检查",
			status: readStatus.status === "受限" || knowledgeStatus.status === "未配置" ? "受限" : "通过",
			tone:
				readStatus.status === "受限" || knowledgeStatus.status === "未配置" ? "warning" : "success",
			businessMeaning: "上线前看 grounding、空回复、敏感信息和 side-effect 风险。",
			input: "最终回复 + evidence_refs + action result",
			output: phase5
				? `decision=${stringifyScalar(phase5.summary.decision)}；${summaryPreview(phase5.summary)}`
				: readStatus.status === "受限" || knowledgeStatus.status === "未配置"
					? "可用于流程验证；不计入真实端到端通过"
					: "通过基础质量检查",
			nextAction: "失败时进入评测运营定位",
			link: { label: "评测运营", to: "/eval" },
		},
		{
			order: "14",
			title: "最终输出与动作结果",
			status: finalStepState.status,
			tone: finalStepState.tone,
			businessMeaning: "把客户会看到的回复和动作结果放在一起，产品可以直接判断体验是否成立。",
			input: "质检后的回复草稿",
			output: result?.reply_text ?? "等待运行后展示最终回复",
			nextAction: "产品验收最终体验",
		},
		{
			order: "15",
			title: "运行记录与审计",
			status: "通过",
			tone: "success",
			businessMeaning: "生成可回看记录，后续可关联测试样本、Trace、PR 和上线证据。",
			input: "run_id + trace_id + mode",
			output: result ? `${result.trace_id}；建议纳入测试集` : "trace-demo-p2-001；建议纳入测试集",
			nextAction: "回到运行记录或评测运营复核",
			link: { label: "运行记录", to: "/debug" },
		},
	];
}

function buildRunFlowStages(
	mode: DataMode,
	customerMessage: string,
	contextMessageCount: number,
	lastSubmittedHistoryCount: number | null,
	result: PipelineMessageResponse | null,
	steps: PipelineStep[],
	runContext: RunFlowBuildContext,
): RunFlowStage[] {
	const phaseByName = new Map(result?.phase_trace.map((entry) => [entry.phase, entry]) ?? []);
	const phase1 = phaseByName.get("phase1_refine");
	const phase2 = phaseByName.get("phase2_recall");
	const phase3 = phaseByName.get("phase3_rerank");
	const phase4 = phaseByName.get("phase4_generate");
	const phase5 = phaseByName.get("phase5_validate");
	const semanticFrame = asRecord(phase2?.summary.semantic_frame);
	const dataModeLabel = DATA_MODES.find((item) => item.id === mode)?.label ?? mode;
	const routingLabel = result ? routingTargetLabel(result) : "无方案命中";
	const runtimeBranch = runtimeBranchLabel(result, phase2);
	const actualAllowedSkills = phaseAllowedSkillsLabel(phase2);
	const skillDecisionPlan = skillDecisionPlanLabel(phase2?.summary.skill_decisions);
	const evidenceRefs = Array.isArray(phase4?.summary.evidence_refs)
		? phase4.summary.evidence_refs.map((item) => String(item))
		: [];
	const evidenceStageStatus = evidenceStageStatusFor(result, phase2);
	const replyStageStatus = replyStageStatusFor(result);
	const submittedHistoryCount = lastSubmittedHistoryCount ?? 0;

	const stages: RunFlowStage[] = [
		{
			id: "input_context",
			title: "用户输入与上下文",
			status: result ? "通过" : runContext.runStatus === "running" ? "运行中" : "跳过",
			tone: result ? "success" : runContext.runStatus === "running" ? "warning" : "neutral",
			summary: result
				? `本轮问题已运行，纳入上文 ${submittedHistoryCount} 条，session=${shortId(result.session_id)}，会话模式=${sessionDisplay(result.runtime_capabilities.session_mode).value}`
				: `先输入一个客户问题。当前页面已准备上文 ${contextMessageCount} 条；不清空记录时，连续发送会默认注入下一轮。`,
			primaryFields: [
				field("本轮问题", customerMessage, "message", "MessagesRequest"),
				field("商户", MERCHANT_ID, "merchant_id", "MessagesRequest"),
				field("数据口径", dataModeLabel, "data_mode_ui", "Admin UI"),
				field(
					"请求上文",
					result ? `${submittedHistoryCount} 条` : `${contextMessageCount} 条待传入`,
					"history",
					"MessagesRequest",
				),
				field("会话", result?.session_id ?? "未返回", "session_id", "MessagesResponse"),
				field("运行记录", result?.trace_id ?? "未返回", "trace_id", "MessagesResponse"),
				field(
					"会话承接",
					result ? sessionDisplay(result.runtime_capabilities.session_mode).value : "未运行",
					"runtime_capabilities.session_mode",
					"MessagesResponse",
				),
				...(runContext.evalContext
					? [
							field("样本编号", runContext.evalContext.sampleId, "sample_id", "Eval replay query"),
							field(
								"评测运行",
								runContext.evalContext.evalRunId ?? "未传入",
								"eval_run_id",
								"Eval replay query",
							),
							field(
								"样本结果",
								runContext.evalContext.sampleResultId ?? "未传入",
								"sample_result_id",
								"Eval replay query",
							),
							field(
								"Badcase",
								runContext.evalContext.badcaseId ?? "未传入",
								"badcase_id",
								"Eval replay query",
							),
							field(
								"期望意图",
								runContext.evalContext.expectedIntent ?? "未传入",
								"expected_intent",
								"Eval replay query",
							),
							field(
								"实际意图",
								runContext.evalContext.actualIntent ?? "未传入",
								"actual_intent",
								"Eval replay query",
							),
							field(
								"期望方案",
								runContext.evalContext.expectedPb ?? "无",
								"expected_pb",
								"Eval replay query",
							),
							field(
								"实际方案",
								runContext.evalContext.actualPb ?? "无",
								"actual_pb",
								"Eval replay query",
							),
							field(
								"根因提示",
								runContext.evalContext.rootCauseHint ?? "未传入",
								"root_cause_hint",
								"Eval replay query",
							),
							field(
								"责任资产",
								runContext.evalContext.responsibleAsset ?? "未传入",
								"responsible_asset",
								"Eval replay query",
							),
							field(
								"badcase 原因",
								runContext.evalContext.badcaseReason ?? "未传入",
								"badcase_reason",
								"Eval replay query",
							),
						]
					: []),
			],
			raw: {
				request: {
					merchant_id: MERCHANT_ID,
					message: customerMessage,
					history_count: result ? submittedHistoryCount : contextMessageCount,
				},
				eval_replay_context: runContext.evalContext,
				result,
			},
		},
		{
			id: "understanding_plan",
			title: "理解与计划",
			status: understandingStageStatusFor(result, phase2),
			tone: understandingStageToneFor(result, phase2),
			summary: result
				? `系统理解为：${semanticTaskLabel(summaryText(phase2?.summary, "semantic_task", ""))}；意图分类=${productIntentLabel(result.intent.intent)}；处理路径=${routingLabel}；分支=${runtimeBranch}`
				: "运行后展示意图分类、语义任务、信息缺口、处理路径和工具计划。",
			primaryFields: [
				field(
					"意图分类",
					result ? productIntentLabel(result.intent.intent) : "未运行",
					"intent.intent",
					"MessagesResponse",
				),
				field(
					"分类把握",
					result ? formatPercent(result.intent.confidence) : "未运行",
					"intent.confidence",
					"MessagesResponse",
				),
				field(
					"当前对话阶段",
					result?.intent.service_stage ?? "未返回",
					"intent.service_stage",
					"MessagesResponse",
				),
				field(
					"客户目标",
					semanticTaskLabel(summaryText(phase2?.summary, "semantic_task", "")),
					"phase2_recall.summary.semantic_task",
					"phase_trace",
				),
				field(
					"信息缺口",
					stringifyScalar(phase2?.summary.missing_slots),
					"phase2_recall.summary.missing_slots",
					"phase_trace",
				),
				field(
					"需要证据",
					stringifyScalar(phase2?.summary.evidence_needs),
					"phase2_recall.summary.evidence_needs",
					"phase_trace",
				),
				field("处理路径", routingLabel, "recommended_playbook", "MessagesResponse"),
				field("方案分支", runtimeBranch, "phase2_recall.summary.selected_branch", "phase_trace"),
				field("匹配方式", matchModeLabel(result?.match_mode), "match_mode", "MessagesResponse"),
				field(
					"运行白名单",
					result ? actualAllowedSkills : "未运行",
					"phase2_recall.summary.allowed_skills",
					"phase_trace",
				),
				field(
					"Skill 判断",
					result ? skillDecisionPlan : "未运行",
					"phase2_recall.summary.skill_decisions",
					"phase_trace",
				),
			],
			raw: { intent: result?.intent, phase1, phase2, semanticFrame },
		},
		{
			id: "evidence",
			title: "事实与证据",
			status: evidenceStageStatus.status,
			tone: evidenceStageStatus.tone,
			summary: result
				? `Shopify 读取=${shopifyReadDisplay(result.runtime_capabilities.shopify_read_mode).value}；知识召回=${ragDisplay(result.runtime_capabilities.rag_mode).value}；证据引用=${evidenceRefs.length} 条`
				: "运行后展示 Shopify、知识与证据、工具事实和证据引用。",
			primaryFields: [
				field(
					"Shopify 读取",
					result
						? shopifyReadDisplay(result.runtime_capabilities.shopify_read_mode).value
						: "未运行",
					"runtime_capabilities.shopify_read_mode",
					"MessagesResponse",
				),
				field(
					"Shopify 写入",
					result
						? shopifyWriteDisplay(result.runtime_capabilities.shopify_write_mode).value
						: "未运行",
					"runtime_capabilities.shopify_write_mode",
					"MessagesResponse",
				),
				field(
					"知识召回",
					result ? ragDisplay(result.runtime_capabilities.rag_mode).value : "未运行",
					"runtime_capabilities.rag_mode",
					"MessagesResponse",
				),
				field(
					"工具结果数",
					stringifyScalar(phase2?.summary.skill_result_count),
					"phase2_recall.summary.skill_result_count",
					"phase_trace",
				),
				field(
					"工具计划",
					result ? skillDecisionPlan : "未运行",
					"phase2_recall.summary.skill_decisions",
					"phase_trace",
				),
				field(
					"消歧候选",
					stringifyScalar(phase2?.summary.ambiguity_options_count),
					"phase2_recall.summary.ambiguity_options_count",
					"phase_trace",
				),
				field(
					"知识片段数",
					stringifyScalar(phase2?.summary.rag_snippet_count),
					"phase2_recall.summary.rag_snippet_count",
					"phase_trace",
				),
				field(
					"回复使用证据",
					evidenceRefs.length > 0 ? evidenceRefs.join(", ") : "本次没有返回证据引用",
					"phase4_generate.summary.evidence_refs",
					"phase_trace",
				),
			],
			raw: { phase2, phase3, evidence_refs: evidenceRefs },
		},
		{
			id: "reply_quality",
			title: "回复与质检",
			status: replyStageStatus.status,
			tone: replyStageStatus.tone,
			summary: result
				? `最终动作=${finalActionLabel(result.final_action_type, result.final_outcome)}；回复生成=${runnerModeLabel(result.runtime_capabilities.phase4_runner_mode)}；质检=${qualityDisplay(result.runtime_capabilities.quality_mode).value}`
				: "运行后展示最终回复、主 Agent 模式、提示词消费状态和质量检查。",
			primaryFields: [
				field("最终回复", result?.reply_text ?? "未运行", "reply_text", "MessagesResponse"),
				field(
					"本轮动作",
					result ? customerActionLabel(result.final_action_type) : "未运行",
					"final_action_type",
					"MessagesResponse",
				),
				field(
					"确认计划",
					result ? confirmationPlanLabel(null, phase4?.summary) : "未运行",
					"phase4_generate.summary.confirmation_plan",
					"phase_trace",
				),
				field("处理结果", result?.final_outcome ?? "未运行", "final_outcome", "MessagesResponse"),
				field(
					"回复生成",
					result ? runnerModeLabel(result.runtime_capabilities.phase4_runner_mode) : "未运行",
					"runtime_capabilities.phase4_runner_mode",
					"MessagesResponse",
				),
				field(
					"主 Agent 回退",
					result ? runnerErrorLabel(phase4?.summary) : "未运行",
					"phase4_generate.summary.runner_error",
					"phase_trace",
				),
				field(
					"Grounding 检查",
					groundingCheckLabel(asRecord(phase5?.summary.grounding_check)),
					"phase5_validate.summary.grounding_check",
					"phase_trace",
				),
				field(
					"Side-effect 检查",
					sideEffectCheckLabel(asRecord(phase5?.summary.side_effect_check)),
					"phase5_validate.summary.side_effect_check",
					"phase_trace",
				),
				field(
					"Step 使用",
					stepUsageLabel(null, phase5?.summary),
					"phase5_validate.summary.step_used",
					"phase_trace",
				),
				field(
					"主提示词",
					result
						? promptEffectDisplay(result.runtime_capabilities.prompt_effect_mode).value
						: "未运行",
					"runtime_capabilities.prompt_effect_mode",
					"MessagesResponse",
				),
				field(
					"生成依据",
					decisionBasisLabel(summaryText(phase4?.summary, "decision_basis", "")),
					"phase4_generate.summary.decision_basis",
					"phase_trace",
				),
				field(
					"质检结论",
					summaryText(phase5?.summary, "decision", "未返回"),
					"phase5_validate.summary.decision",
					"phase_trace",
				),
				field(
					"质检模式",
					result ? qualityDisplay(result.runtime_capabilities.quality_mode).value : "未运行",
					"runtime_capabilities.quality_mode",
					"MessagesResponse",
				),
			],
			raw: { phase4, phase5, output: result?.reply_text },
		},
		{
			id: "audit",
			title: "审计与沉淀",
			status: result ? "通过" : "跳过",
			tone: result ? "success" : "neutral",
			summary: result
				? `trace=${shortId(result.trace_id)}，session=${shortId(result.session_id)}，可在运行记录复盘。`
				: "运行后生成 trace/session，并可保存为测试样本候选。",
			primaryFields: [
				field("运行记录", result?.trace_id ?? "未返回", "trace_id", "MessagesResponse"),
				field("会话", result?.session_id ?? "未返回", "session_id", "MessagesResponse"),
				field(
					"Phase 数量",
					result ? String(result.phase_trace.length) : "未运行",
					"phase_trace",
					"MessagesResponse",
				),
				field("建议", result ? "保存为样本候选" : "运行后可保存", "sample_candidate", "Admin UI"),
				field("旧版节点数", String(steps.length), "pipeline_steps", "Admin UI"),
			],
			raw: { result, steps },
		},
	];
	return decorateRunFlowStagesWithEvents(stages, runContext);
}

function decorateRunFlowStagesWithEvents(
	stages: RunFlowStage[],
	runContext: RunFlowBuildContext,
): RunFlowStage[] {
	if (runContext.eventSource !== "admin_run") {
		if (runContext.eventSource !== "messages_fallback") return stages;
		return stages.map((stage) => ({
			...stage,
			primaryFields: [
				field(
					"事件来源",
					"运行事件不可用，已回退最终消息结果",
					"run_event_source",
					"Admin UI fallback",
				),
				...stage.primaryFields,
			],
			raw: {
				run_event_source: "messages_fallback",
				stage_raw: stage.raw,
			},
		}));
	}

	const eventsByStage = new Map<RunFlowStageId, PipelineAdminRunEvent[]>();
	for (const event of runContext.events) {
		const current = eventsByStage.get(event.stage_id) ?? [];
		current.push(event);
		eventsByStage.set(event.stage_id, current);
	}
	const totalEventsByStage = new Map<RunFlowStageId, number>();
	for (const event of runContext.allEvents) {
		totalEventsByStage.set(event.stage_id, (totalEventsByStage.get(event.stage_id) ?? 0) + 1);
	}

	return stages.map((stage) => {
		const events = eventsByStage.get(stage.id) ?? [];
		const latestEvent = events.at(-1);
		const eventState = statusFromEvents(
			events,
			runContext.runStatus,
			totalEventsByStage.get(stage.id) ?? 0,
		);
		const eventSummary = latestEvent
			? `${stage.summary} · 最新事件：${latestEvent.title}（${eventStatusLabel(latestEvent.status)}）`
			: stage.summary;
		return {
			...stage,
			status: eventState.status,
			tone: eventState.tone,
			summary: eventSummary,
			primaryFields: [
				field(
					"事件来源",
					`AdminRunEvent ${events.length}/${totalEventsByStage.get(stage.id) ?? 0}`,
					"admin_run.events",
					"AdminRunEvent",
				),
				...(latestEvent
					? [
							field("最新事件", latestEvent.title, "events[].title", "AdminRunEvent"),
							field("事件类型", latestEvent.event_type, "events[].event_type", "AdminRunEvent"),
							field(
								"事件状态",
								eventStatusLabel(latestEvent.status),
								"events[].status",
								"AdminRunEvent",
							),
							field("事件说明", latestEvent.summary, "events[].summary", "AdminRunEvent"),
						]
					: []),
				...stage.primaryFields,
			],
			raw: {
				run_event_source: "admin_run",
				visible_events: events,
				all_event_count: runContext.allEvents.length,
				stage_raw: stage.raw,
			},
		};
	});
}

function statusFromEvents(
	events: PipelineAdminRunEvent[],
	runStatus: RunStatus,
	totalStageEvents: number,
): Pick<RunFlowStage, "status" | "tone"> {
	if (events.some((event) => event.status === "failed" || event.status === "blocked")) {
		return { status: "受限", tone: "blocked" };
	}
	if (events.some((event) => event.status === "warning"))
		return { status: "受限", tone: "warning" };
	if (events.some((event) => event.status === "running"))
		return { status: "运行中", tone: "warning" };
	if (events.some((event) => event.status === "success"))
		return { status: "通过", tone: "success" };
	if (events.length > 0 && events.every((event) => event.status === "skipped")) {
		return { status: "跳过", tone: "neutral" };
	}
	if (runStatus === "running" || totalStageEvents > 0) return { status: "运行中", tone: "warning" };
	return { status: "跳过", tone: "neutral" };
}

function eventStatusLabel(status: PipelineAdminRunEvent["status"]): string {
	const labels: Record<PipelineAdminRunEvent["status"], string> = {
		running: "运行中",
		success: "通过",
		warning: "受限",
		blocked: "阻塞",
		failed: "失败",
		skipped: "跳过",
	};
	return labels[status];
}

function field(label: string, value: string, rawName: string, source: string) {
	return { label, value: value || "未返回", rawName, source };
}

function isAuditRunEvent(event: PipelineAdminRunEvent): boolean {
	return (
		event.stage_id === "audit" ||
		event.event_type === "trace_persisted" ||
		event.event_type === "run_completed"
	);
}

function runEventStatus(status: PipelineAdminRunEvent["status"]): {
	status: StepStatus;
	tone: StepTone;
} {
	if (status === "failed" || status === "blocked") return { status: "受限", tone: "blocked" };
	if (status === "warning") return { status: "受限", tone: "warning" };
	if (status === "running") return { status: "运行中", tone: "warning" };
	if (status === "skipped") return { status: "跳过", tone: "neutral" };
	return { status: "通过", tone: "success" };
}

function runEventValue(
	record: Record<string, unknown> | null | undefined,
	key: string,
	fallback = "未返回",
): string {
	if (!record || !(key in record)) return fallback;
	return stringifyScalar(record[key]);
}

function runEventRoutingTarget(
	event: PipelineAdminRunEvent,
	result: PipelineMessageResponse | null,
): string {
	const shortCircuit = event.decision.short_circuit_skill;
	if (typeof shortCircuit === "string" && shortCircuit.trim()) return shortCircuit;
	const playbookId = event.decision.playbook_id;
	if (typeof playbookId === "string" && playbookId.trim()) return playbookId;
	return result ? routingTargetLabel(result) : "无方案命中";
}

function phaseSummary(
	result: PipelineMessageResponse | null,
	phase: string,
): Record<string, unknown> | null {
	return result?.phase_trace.find((entry) => entry.phase === phase)?.summary ?? null;
}

function eventCountValue(
	event: PipelineAdminRunEvent,
	result: PipelineMessageResponse | null,
	keys: string[],
	phaseNames: string[] = [],
): string {
	for (const key of keys) {
		const value = summaryNumber(event.output[key] ?? event.decision[key] ?? event.input[key]);
		if (value !== null) return String(value);
	}
	for (const phase of phaseNames) {
		const summary = phaseSummary(result, phase);
		for (const key of keys) {
			const value = summaryNumber(summary?.[key]);
			if (value !== null) return String(value);
		}
	}
	return "0";
}

function runEventChips(
	event: PipelineAdminRunEvent,
	result: PipelineMessageResponse | null,
): string[] {
	const phase2 = phaseSummary(result, "phase2_recall");
	const phase4 = phaseSummary(result, "phase4_generate");
	const chips: string[] = [];
	switch (event.event_type) {
		case "message_received":
			chips.push(`请求上文=${runEventValue(event.input, "request_history_count", "0")} 条`);
			break;
		case "intent_completed":
			chips.push(
				`意图分类=${runEventValue(event.output, "intent", result?.intent.intent ?? "未返回")}`,
			);
			chips.push(
				`置信度=${runEventValue(event.output, "confidence", result ? formatPercent(result.intent.confidence) : "未返回")}`,
			);
			break;
		case "semantic_frame_completed":
			chips.push(
				`语义任务=${semanticTaskLabel(
					runEventValue(event.output, "semantic_task", summaryText(phase2, "semantic_task", "")),
				)}`,
			);
			chips.push(
				`缺口=${runEventValue(event.output, "missing_slots", stringifyScalar(phase2?.missing_slots))}`,
			);
			break;
		case "playbook_recalled":
			chips.push(`处理路径=${runEventRoutingTarget(event, result)}`);
			chips.push(
				`分支=${runEventValue(
					event.decision,
					"selected_branch_display_name",
					runtimeBranchLabel(result, phase2),
				)}`,
			);
			chips.push(
				`匹配=${runEventValue(event.decision, "match_mode", result?.match_mode ?? "未返回")}`,
			);
			break;
		case "evidence_fetch_started":
			chips.push(
				`证据需求=${runEventValue(event.output, "evidence_needs", stringifyScalar(phase2?.evidence_needs))}`,
			);
			break;
		case "tool_completed":
			chips.push(
				`工具事实 ${eventCountValue(event, result, ["skill_result_count"], ["phase2_recall"])} 条`,
			);
			chips.push(
				`消歧候选 ${eventCountValue(event, result, ["ambiguity_options_count"], ["phase2_recall"])} 个`,
			);
			break;
		case "rag_completed":
			chips.push(
				`知识片段 ${eventCountValue(event, result, ["rag_snippet_count"], ["phase2_recall", "phase3_rerank"])} 条`,
			);
			break;
		case "generation_completed":
			chips.push(
				`对客动作=${runEventValue(event.decision, "action_type", summaryText(phase4, "action_type", "未返回"))}`,
			);
			chips.push(`主提示词=${promptSnapshotLabel(event)}`);
			break;
		case "quality_completed":
			chips.push(
				`质检=${runEventValue(event.output, "decision", summaryText(phaseSummary(result, "phase5_validate"), "decision", "未返回"))}`,
			);
			break;
		default:
			if (event.field_refs.length > 0) chips.push(`字段 ${event.field_refs.length} 项`);
			if (event.evidence_refs.length > 0) chips.push(`证据 ${event.evidence_refs.length} 条`);
			break;
	}
	return chips.filter(Boolean);
}

function promptSnapshotLabel(event: PipelineAdminRunEvent): string {
	const snapshot = asRecord(event.raw.prompt_snapshot);
	if (!snapshot) return "未返回快照";
	return `已返回 ${Object.keys(snapshot).length} 项`;
}

function buildRunEventInspectorStage(
	event: PipelineAdminRunEvent,
	result: PipelineMessageResponse | null,
): RunDetailStage<string> {
	const eventState = runEventStatus(event.status);
	return {
		id: `event:${event.event_id}`,
		title: event.title,
		status: eventState.status,
		tone: eventState.tone,
		summary: event.summary || "后端事件未返回说明。",
		primaryFields: buildRunEventFields(event, result),
		raw: {
			admin_run_event: event,
			related_messages_response: result,
		},
	};
}

function buildRunEventFields(
	event: PipelineAdminRunEvent,
	result: PipelineMessageResponse | null,
): RunDetailField[] {
	const phase2 = phaseSummary(result, "phase2_recall");
	const phase4 = phaseSummary(result, "phase4_generate");
	const phase5 = phaseSummary(result, "phase5_validate");
	const baseFields: RunDetailField[] = [
		field("事件类型", event.event_type, "event_type", "AdminRunEvent"),
		field("阶段来源", event.stage_id, "stage_id", "AdminRunEvent"),
		field("事件状态", eventStatusLabel(event.status), "status", "AdminRunEvent"),
		field("事件说明", event.summary, "summary", "AdminRunEvent"),
	];
	const timingFields: RunDetailField[] = [
		field("时间消耗", runEventTimingLabel(event), "duration_ms/offset_ms", "AdminRunEvent"),
		field("阶段耗时", formatDurationMs(event.duration_ms), "duration_ms", "AdminRunEvent"),
		field(
			"距运行开始",
			numericTiming(event.offset_ms) !== null ? `+${formatDurationMs(event.offset_ms)}` : "未返回",
			"offset_ms",
			"AdminRunEvent",
		),
		field("时间来源", timingSourceLabel(event.timing_source), "timing_source", "AdminRunEvent"),
		field("口径说明", timingSourceHelp(event), "timing_source", "AdminRunEvent"),
	];
	const fieldsByType: Record<string, RunDetailField[]> = {
		run_started: [
			field(
				"商户",
				runEventValue(event.input, "merchant_id", MERCHANT_ID),
				"input.merchant_id",
				"AdminRunEvent",
			),
			field(
				"数据模式",
				runEventValue(event.input, "data_mode"),
				"input.data_mode",
				"AdminRunEvent",
			),
		],
		message_received: [
			field("客户问题", runEventValue(event.input, "message"), "input.message", "AdminRunEvent"),
			field(
				"请求上文",
				`${runEventValue(event.input, "request_history_count", "0")} 条`,
				"input.request_history_count",
				"AdminRunEvent",
			),
			field("会话", event.session_id ?? "未返回", "session_id", "AdminRunEvent"),
		],
		context_loaded: [
			field(
				"请求上文",
				`${runEventValue(event.input, "request_history_count", "0")} 条`,
				"input.request_history_count",
				"AdminRunEvent",
			),
			field(
				"已纳入持久上文",
				`${runEventValue(event.output, "history_count", "0")} 轮`,
				"output.history_count",
				"AdminRunEvent",
			),
			field("会话", event.session_id ?? "未返回", "session_id", "AdminRunEvent"),
		],
		intent_completed: [
			field(
				"意图分类",
				runEventValue(event.output, "intent", result?.intent.intent ?? "未返回"),
				"output.intent",
				"AdminRunEvent",
			),
			field(
				"分类把握",
				runEventValue(
					event.output,
					"confidence",
					result ? formatPercent(result.intent.confidence) : "未返回",
				),
				"output.confidence",
				"AdminRunEvent",
			),
			field(
				"话题",
				runEventValue(event.output, "topic", result?.intent.topic ?? "未返回"),
				"output.topic",
				"AdminRunEvent",
			),
		],
		semantic_frame_completed: [
			field(
				"客户目标",
				runEventValue(event.output, "user_goal", summaryText(phase2, "user_goal", "未返回")),
				"output.user_goal",
				"AdminRunEvent",
			),
			field(
				"语义任务",
				semanticTaskLabel(
					runEventValue(event.output, "semantic_task", summaryText(phase2, "semantic_task", "")),
				),
				"output.semantic_task",
				"AdminRunEvent",
			),
			field(
				"信息缺口",
				runEventValue(event.output, "missing_slots", stringifyScalar(phase2?.missing_slots)),
				"output.missing_slots",
				"AdminRunEvent",
			),
			field(
				"需要证据",
				runEventValue(event.output, "evidence_needs", stringifyScalar(phase2?.evidence_needs)),
				"output.evidence_needs",
				"AdminRunEvent",
			),
		],
		playbook_recalled: [
			field(
				"处理路径",
				runEventRoutingTarget(event, result),
				"decision.playbook_id / decision.short_circuit_skill",
				"AdminRunEvent",
			),
			field(
				"方案分支",
				runEventValue(
					event.decision,
					"selected_branch_display_name",
					runtimeBranchLabel(result, phase2),
				),
				"decision.selected_branch_display_name",
				"AdminRunEvent",
			),
			field(
				"匹配方式",
				runEventValue(event.decision, "match_mode", result?.match_mode ?? "未返回"),
				"decision.match_mode",
				"AdminRunEvent",
			),
			field(
				"运行白名单",
				runEventValue(event.output, "allowed_skills", phaseAllowedSkillsLabel(phase2)),
				"output.allowed_skills",
				"AdminRunEvent",
			),
		],
		evidence_fetch_started: [
			field(
				"证据需求",
				runEventValue(event.output, "evidence_needs", stringifyScalar(phase2?.evidence_needs)),
				"output.evidence_needs",
				"AdminRunEvent",
			),
			field(
				"信息缺口",
				runEventValue(event.output, "missing_slots", stringifyScalar(phase2?.missing_slots)),
				"output.missing_slots",
				"AdminRunEvent",
			),
		],
		tool_completed: [
			field(
				"工具事实数",
				eventCountValue(event, result, ["skill_result_count"], ["phase2_recall"]),
				"output.skill_result_count",
				"AdminRunEvent/phase2_recall",
			),
			field(
				"消歧候选数",
				eventCountValue(event, result, ["ambiguity_options_count"], ["phase2_recall"]),
				"output.ambiguity_options_count",
				"AdminRunEvent/phase2_recall",
			),
			field(
				"Shopify 读取",
				result ? shopifyReadDisplay(result.runtime_capabilities.shopify_read_mode).value : "未返回",
				"runtime_capabilities.shopify_read_mode",
				"MessagesResponse",
			),
		],
		rag_completed: [
			field(
				"知识片段数",
				eventCountValue(event, result, ["rag_snippet_count"], ["phase2_recall", "phase3_rerank"]),
				"output.rag_snippet_count",
				"AdminRunEvent/phase2_recall/phase3_rerank",
			),
			field(
				"知识召回模式",
				result ? ragDisplay(result.runtime_capabilities.rag_mode).value : "未返回",
				"runtime_capabilities.rag_mode",
				"MessagesResponse",
			),
		],
		generation_completed: [
			field(
				"对客动作",
				runEventValue(event.decision, "action_type", summaryText(phase4, "action_type", "未返回")),
				"decision.action_type",
				"AdminRunEvent/phase4_generate",
			),
			field(
				"最终回复",
				runEventValue(event.output, "reply_text", result?.reply_text ?? "未返回"),
				"output.reply_text",
				"AdminRunEvent/MessagesResponse",
			),
			field(
				"生成依据",
				runEventValue(
					event.decision,
					"decision_basis",
					summaryText(phase4, "decision_basis", "未返回"),
				),
				"decision.decision_basis",
				"AdminRunEvent/phase4_generate",
			),
			field(
				"回复使用证据",
				runEventValue(event.output, "evidence_refs", stringifyScalar(phase4?.evidence_refs)),
				"output.evidence_refs",
				"AdminRunEvent/phase4_generate",
			),
			field(
				"工具 loop",
				loopExecutionLabel(event.output, phase4),
				"output.loop_meta / output.executed_actions",
				"AdminRunEvent/phase4_generate",
			),
			field(
				"主 Agent 回退",
				runnerErrorLabel(phase4),
				"phase4_generate.summary.runner_error",
				"AdminRunEvent/phase4_generate",
			),
			field(
				"确认计划",
				confirmationPlanLabel(event.output, phase4),
				"output.confirmation_plan / output.confirmation_status",
				"AdminRunEvent/phase4_generate",
			),
			field("主提示词快照", promptSnapshotLabel(event), "raw.prompt_snapshot", "AdminRunEvent raw"),
			field(
				"回复生成模式",
				result ? runnerModeLabel(result.runtime_capabilities.phase4_runner_mode) : "未返回",
				"runtime_capabilities.phase4_runner_mode",
				"MessagesResponse",
			),
		],
		quality_completed: [
			field(
				"质检结论",
				runEventValue(event.output, "decision", summaryText(phase5, "decision", "未返回")),
				"output.decision",
				"AdminRunEvent/phase5_validate",
			),
			field(
				"Grounding 检查",
				groundingCheckLabel(
					asRecord(event.output.grounding_check) ?? asRecord(phase5?.grounding_check),
				),
				"output.grounding_check / phase5_validate.summary.grounding_check",
				"AdminRunEvent/phase5_validate",
			),
			field(
				"Side-effect 检查",
				sideEffectCheckLabel(
					asRecord(event.output.side_effect_check) ?? asRecord(phase5?.side_effect_check),
				),
				"output.side_effect_check / phase5_validate.summary.side_effect_check",
				"AdminRunEvent/phase5_validate",
			),
			field(
				"Step 使用",
				stepUsageLabel(event.output, phase5),
				"output.step_used / phase5_validate.summary.step_used",
				"AdminRunEvent/phase5_validate",
			),
			field(
				"阻断原因",
				runEventValue(event.output, "block_reason", summaryText(phase5, "block_reason", "无")),
				"output.block_reason",
				"AdminRunEvent/phase5_validate",
			),
			field(
				"质检模式",
				result ? qualityDisplay(result.runtime_capabilities.quality_mode).value : "未返回",
				"runtime_capabilities.quality_mode",
				"MessagesResponse",
			),
		],
	};
	const payloadFields = [
		...(Object.keys(event.input).length > 0
			? [field("输入摘要", summaryPreview(event.input), "input", "AdminRunEvent")]
			: []),
		...(Object.keys(event.decision).length > 0
			? [field("判断摘要", summaryPreview(event.decision), "decision", "AdminRunEvent")]
			: []),
		...(Object.keys(event.output).length > 0
			? [field("输出摘要", summaryPreview(event.output), "output", "AdminRunEvent")]
			: []),
		field(
			"证据引用",
			event.evidence_refs.length > 0 ? event.evidence_refs.join(", ") : "本事件未直接返回",
			"evidence_refs",
			"AdminRunEvent",
		),
		field(
			"字段来源",
			event.field_refs.length > 0 ? event.field_refs.join(", ") : "未返回",
			"field_refs",
			"AdminRunEvent",
		),
	];
	return [
		...baseFields,
		...timingFields,
		...(fieldsByType[event.event_type] ?? []),
		...payloadFields,
	];
}

function createContextMessage(role: ContextRole, content = ""): ContextMessage {
	return {
		id:
			typeof crypto !== "undefined" && "randomUUID" in crypto
				? crypto.randomUUID()
				: `${role}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
		role,
		content,
	};
}

function TraceChatConsole({
	mode,
	selectedModeLabel,
	customerMessage,
	contextMessages,
	status,
	error,
	result,
	lastRunCustomerMessage,
	stages,
	runEvents,
	allRunEventCount,
	totalLatencyMs,
	runEventSource,
	evalContext,
	activeStageId,
	onSelectStage,
	stageRunning,
	onModeChange,
	onCustomerMessageChange,
	onClearConversation,
	onFullRun,
	onIntentRun,
	onPlaybookRun,
	onToolRun,
	onKnowledgeRun,
	onPromptRun,
	onQualityRun,
}: {
	mode: DataMode;
	selectedModeLabel: string;
	customerMessage: string;
	contextMessages: ContextMessage[];
	status: RunStatus;
	error: string | null;
	result: PipelineMessageResponse | null;
	lastRunCustomerMessage: string | null;
	stages: RunFlowStage[];
	runEvents: PipelineAdminRunEvent[];
	allRunEventCount: number;
	totalLatencyMs: number | null;
	runEventSource: RunEventSource;
	evalContext: EvalReplayContext | null;
	activeStageId: RunFlowStageId;
	onSelectStage: (stageId: RunFlowStageId) => void;
	stageRunning: StageKind | null;
	onModeChange: (mode: DataMode) => void;
	onCustomerMessageChange: (message: string) => void;
	onClearConversation: () => void;
	onFullRun: () => void;
	onIntentRun: () => void;
	onPlaybookRun: () => void;
	onToolRun: () => void;
	onKnowledgeRun: () => void;
	onPromptRun: () => void;
	onQualityRun: () => void;
}) {
	const [boundaryDialogOpen, setBoundaryDialogOpen] = useState(false);
	const [activeRunEventId, setActiveRunEventId] = useState<string | null>(null);
	const [eventSelectionPinned, setEventSelectionPinned] = useState(false);
	const [promptSnapshotEvent, setPromptSnapshotEvent] = useState<PipelineAdminRunEvent | null>(
		null,
	);
	const [singleStageOpen, setSingleStageOpen] = useState(() => {
		return typeof window !== "undefined" && window.location.hash === "#single-stage-diagnostics";
	});
	const activeStage = stages.find((stage) => stage.id === activeStageId) ?? stages[0];
	const timelineEvents = useMemo(
		() => runEvents.filter((event) => !isAuditRunEvent(event)),
		[runEvents],
	);
	const activeRunEvent =
		timelineEvents.find((event) => event.event_id === activeRunEventId) ??
		timelineEvents.at(-1) ??
		null;
	const activeInspectorStage = activeRunEvent
		? buildRunEventInspectorStage(activeRunEvent, result)
		: activeStage;
	let assistantReply = result?.reply_text ?? "运行后这里显示客户会看到的最终回复。";
	if (status === "running") {
		assistantReply = "正在运行主链路，请稍候。";
	}
	const latestOffsetMs = latestEventOffsetMs(runEvents);
	const runMeta = result
		? `trace=${shortId(result.trace_id)} · session=${shortId(result.session_id)}`
		: status === "error"
			? error || "主链路调用失败"
			: "等待运行";
	const timingMeta =
		totalLatencyMs !== null
			? `总耗时 ${formatDurationMs(totalLatencyMs)}`
			: status === "running" && latestOffsetMs !== null
				? `已运行 +${formatDurationMs(latestOffsetMs)}`
				: "耗时等待回传";
	const lastContextUser = contextMessages.at(-2);
	const lastContextAssistant = contextMessages.at(-1);
	const currentRunIsCommitted = Boolean(
		result &&
			lastRunCustomerMessage &&
			lastContextUser?.role === "user" &&
			lastContextUser.content === lastRunCustomerMessage &&
			lastContextAssistant?.role === "assistant" &&
			lastContextAssistant.content === result.reply_text.trim(),
	);
	const visibleHistory = currentRunIsCommitted ? contextMessages.slice(0, -2) : contextMessages;
	const shouldShowLastRunBubble = status === "running" || Boolean(result && lastRunCustomerMessage);
	const fallbackCustomerMessage = lastRunCustomerMessage ?? "";
	const activeCustomerBubble = shouldShowLastRunBubble
		? fallbackCustomerMessage
		: customerMessage.trim();
	const selectedMode = DATA_MODES.find((item) => item.id === mode);
	const sampleLabel = evalContext
		? `${evalContext.source === "p10_tool_audit" ? "P10 工具审计" : "Golden15"} · ${evalContext.sampleId}`
		: "Golden15 · 发货/改地址";
	const eventSourceLabel =
		runEventSource === "admin_run_stream"
			? `实时运行事件 · ${runEvents.length}/${allRunEventCount} 事件`
			: runEventSource === "admin_run"
				? `运行事件 · ${runEvents.length}/${allRunEventCount} 事件`
				: runEventSource === "messages_fallback"
					? "运行事件不可用，已回退最终消息结果"
					: "等待运行事件";
	const evalProjectionContext = evalContext
		? {
				sourceLabel:
					evalContext.source === "p10_tool_audit" ? "P10 单环节样本上下文" : "P2 复跑上下文",
				sampleId: evalContext.sampleId,
				message: activeCustomerBubble || customerMessage || lastRunCustomerMessage,
				expectedIntent: evalContext.expectedIntent,
				actualIntent: evalContext.actualIntent,
				expectedPb: evalContext.expectedPb,
				actualPb: evalContext.actualPb,
				rootCauseHint: evalContext.rootCauseHint,
				responsibleAsset: evalContext.responsibleAsset,
				badcaseReason: evalContext.badcaseReason,
				evalRunId: evalContext.evalRunId,
				sampleResultId: evalContext.sampleResultId,
				badcaseId: evalContext.badcaseId,
			}
		: null;

	useEffect(() => {
		if (timelineEvents.length === 0) {
			setActiveRunEventId(null);
			setEventSelectionPinned(false);
			return;
		}
		const latestEventId = timelineEvents.at(-1)?.event_id ?? null;
		const selectedEventStillVisible = timelineEvents.some(
			(event) => event.event_id === activeRunEventId,
		);
		if (!eventSelectionPinned || !selectedEventStillVisible) {
			setActiveRunEventId(latestEventId);
		}
	}, [activeRunEventId, eventSelectionPinned, timelineEvents]);

	useEffect(() => {
		if (typeof window === "undefined") return;
		const handleHashChange = () => {
			if (window.location.hash === "#single-stage-diagnostics") {
				setSingleStageOpen(true);
			}
		};
		handleHashChange();
		window.addEventListener("hashchange", handleHashChange);
		return () => window.removeEventListener("hashchange", handleHashChange);
	}, []);

	return (
		<section className="space-y-4" data-testid="trace-chat-console">
			<div
				className="rounded-md border border-surface-border bg-surface-card px-4 py-3"
				data-testid="pipeline-parameter-bar"
			>
				<div className="grid gap-3 lg:grid-cols-[minmax(150px,0.9fr)_minmax(190px,1fr)_minmax(180px,0.9fr)_minmax(150px,0.8fr)_auto_auto] lg:items-end">
					<label className="grid gap-1 text-sm">
						<span className="text-xs font-medium text-text-muted">商户</span>
						<input
							value={MERCHANT_ID}
							readOnly
							className="h-10 rounded-md border border-surface-border bg-surface-subtle px-3 font-mono text-sm text-text-secondary"
						/>
					</label>
					<label className="grid gap-1 text-sm">
						<span className="text-xs font-medium text-text-muted">测试样本</span>
						<input
							value={sampleLabel}
							readOnly
							className="h-10 rounded-md border border-surface-border bg-surface-subtle px-3 text-sm text-text-secondary"
						/>
					</label>
					<div className="grid gap-1 text-sm">
						<div className="flex items-center justify-between gap-2">
							<label htmlFor="pipeline-data-mode" className="text-xs font-medium text-text-muted">
								数据模式
							</label>
							<DataModeHelp selectedModeId={mode} />
						</div>
						<Select
							id="pipeline-data-mode"
							aria-label="数据模式"
							className="w-full"
							value={mode}
							onChange={(event) => onModeChange(event.target.value as DataMode)}
						>
							{DATA_MODES.map((item) => (
								<option key={item.id} value={item.id}>
									{item.label} · {item.state}
								</option>
							))}
						</Select>
					</div>
					<label className="grid gap-1 text-sm">
						<span className="text-xs font-medium text-text-muted">焦点对象</span>
						<input
							value="未指定"
							readOnly
							className="h-10 rounded-md border border-surface-border bg-surface-subtle px-3 text-sm text-text-secondary"
						/>
					</label>
					<Button
						type="button"
						variant="outline"
						className="h-10"
						onClick={() => setBoundaryDialogOpen(true)}
					>
						运行边界详情
					</Button>
					<Button type="button" variant="outline" className="h-10" onClick={onClearConversation}>
						<Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
						清空记录
					</Button>
				</div>
				<div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-text-muted">
					<StatusPill
						tone={status === "error" ? "blocked" : status === "running" ? "warning" : "neutral"}
					>
						{`当前模式：${selectedModeLabel}`}
					</StatusPill>
					<span data-testid="data-mode-current-guidance">
						{selectedMode?.guidance ?? "请选择本次测试数据口径。"}
					</span>
					<span>上下文 {contextMessages.length} 条；不清空记录会默认注入下一轮。</span>
					{evalContext ? (
						<span className="rounded border border-brand-warning/30 bg-brand-warning/10 px-2 py-1 text-brand-warning">
							评测运营样本 {sampleLabel}：期望 {evalContext.expectedIntent ?? "未传入"} / 实际{" "}
							{evalContext.actualIntent ?? "未传入"}；方案期望 {evalContext.expectedPb ?? "无"} /
							实际 {evalContext.actualPb ?? "无"}
							{evalContext.rootCauseHint || evalContext.responsibleAsset ? (
								<>
									；归因 {evalContext.rootCauseHint ?? "待确认"} /{" "}
									{evalContext.responsibleAsset ?? "责任资产待确认"}
								</>
							) : null}
						</span>
					) : null}
				</div>
			</div>

			{evalProjectionContext ? <EvalSampleProjection context={evalProjectionContext} /> : null}

			<div className="grid gap-4 xl:grid-cols-[minmax(0,1.65fr)_420px]">
				<div
					className="flex min-h-[720px] flex-col rounded-md border border-surface-border bg-surface-card"
					data-testid="chat-simulator"
				>
					<div className="border-b border-surface-border px-4 py-3">
						<div className="flex items-center justify-between gap-3">
							<div>
								<div className="text-xs font-medium text-text-muted">应答过程回放</div>
								<h3 className="mt-1 text-base font-semibold text-text-primary">客户对话模拟</h3>
								<p className="mt-1 text-sm text-text-secondary">
									按真实聊天顺序展示历史消息、本轮输入、Agent 处理过程和最终回复。
								</p>
							</div>
							<StatusPill
								tone={
									status === "error"
										? "blocked"
										: status === "running"
											? "warning"
											: result
												? "success"
												: "neutral"
								}
							>
								{status === "running"
									? "运行中"
									: result
										? "已生成"
										: status === "error"
											? "失败"
											: "待运行"}
							</StatusPill>
						</div>
					</div>
					<div className="flex-1 space-y-4 overflow-auto bg-surface-subtle p-4">
						{visibleHistory.length > 0 ? (
							visibleHistory.map((message, index) => (
								<ChatBubble
									key={message.id}
									speaker={message.role}
									label={`上文 ${index + 1}`}
									content={message.content}
								/>
							))
						) : (
							<div className="rounded-md border border-dashed border-surface-border bg-surface-card px-3 py-3 text-sm text-text-secondary">
								当前没有历史消息。连续发送后，上一轮客户和助手消息会自动作为下一轮上文。
							</div>
						)}

						{activeCustomerBubble ? (
							<ChatBubble
								speaker="user"
								label={status === "running" || result ? "本轮客户消息" : "待发送客户消息"}
								content={activeCustomerBubble}
								emphasized
							/>
						) : null}

						<div
							className="ml-0 max-w-[94%] rounded-md border border-surface-border bg-surface-card p-3 shadow-sm"
							data-testid="agent-process-bubble"
							data-run-event-source={runEventSource}
						>
							<div className="flex items-start justify-between gap-3">
								<div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
									<Bot className="h-4 w-4 text-brand-primary" aria-hidden="true" />
									Agent 运行过程
								</div>
								<div className="text-right text-xs text-text-muted">
									<div>{runMeta}</div>
									<div>{timingMeta}</div>
									<div>{eventSourceLabel}</div>
								</div>
							</div>
							{timelineEvents.length > 0 ? (
								<RunEventTimeline
									events={timelineEvents}
									activeEventId={activeRunEvent?.event_id ?? null}
									result={result}
									onSelectEvent={(event) => {
										setActiveRunEventId(event.event_id);
										setEventSelectionPinned(true);
										onSelectStage(event.stage_id);
									}}
								/>
							) : (
								<div
									className="mt-3 rounded-md border border-dashed border-surface-border bg-surface-subtle px-3 py-3 text-sm leading-6 text-text-secondary"
									data-testid="run-event-timeline"
								>
									输入客户问题并发送后，这里会按真实运行事件展示每一步处理过程。没有运行时不预先展开阶段，避免把计划当成已发生事实。
									<div className="mt-2">
										<button
											type="button"
											className="text-sm font-medium text-brand-primary hover:underline"
											onClick={() => onSelectStage("input_context")}
										>
											查看输入与上文明细
										</button>
									</div>
								</div>
							)}
						</div>

						<div
							className="ml-0 max-w-[86%] rounded-md border border-surface-border bg-surface-card px-4 py-3"
							data-testid="assistant-final-bubble"
						>
							<div className="text-xs font-medium text-text-muted">助手回复 · {runMeta}</div>
							<p className="mt-2 text-sm leading-6 text-text-primary">{assistantReply}</p>
						</div>
					</div>
					<div className="border-t border-surface-border bg-surface-card p-4">
						<label className="grid gap-1 text-sm">
							<span className="text-xs font-medium text-text-muted">当前客户消息</span>
							<textarea
								aria-label="当前客户消息 客户问题"
								value={customerMessage}
								onChange={(event) => onCustomerMessageChange(event.target.value)}
								className="min-h-24 rounded-md border border-surface-border bg-surface-background p-3 text-sm leading-6"
							/>
						</label>
						<div className="mt-3 flex flex-wrap gap-2">
							<Button
								type="button"
								onClick={onFullRun}
								disabled={status === "running" || !customerMessage.trim()}
							>
								<PlayCircle className="mr-2 h-4 w-4" aria-hidden="true" />
								{status === "running" ? "运行中..." : "发送并运行"}
							</Button>
							<details
								id="single-stage-diagnostics"
								open={singleStageOpen}
								onToggle={(event) => setSingleStageOpen(event.currentTarget.open)}
								className="group rounded-md border border-surface-border bg-surface-background px-3 py-2 text-sm"
								data-testid="single-stage-diagnostics"
							>
								<summary className="cursor-pointer list-none font-medium text-text-secondary">
									高级诊断
								</summary>
								<p className="mt-2 max-w-2xl text-xs leading-5 text-text-muted">
									各环节测试已放回对应能力页：分类测试、方案测试、动作验证、检索与验证、沙盒与发布前评测、质量策略。本区域只保留兼容入口和研发
									/ QA 排障。
								</p>
								<div className="mt-3 rounded-md border border-surface-border bg-white p-3 text-xs leading-5 text-text-secondary">
									产品主路径已经迁移到各能力页；这里的按钮仅用于兼容历史回放和研发快速排障，不能作为产品运营的主入口。
								</div>
								<div className="mt-3 flex flex-wrap gap-2">
									<Button
										type="button"
										variant="outline"
										onClick={onIntentRun}
										disabled={stageRunning !== null || !customerMessage.trim()}
									>
										{stageRunning === "intent" ? "意图运行中..." : "只跑意图"}
									</Button>
									<Button
										type="button"
										variant="outline"
										onClick={onPlaybookRun}
										disabled={stageRunning !== null || !customerMessage.trim()}
									>
										{stageRunning === "playbook" ? "方案运行中..." : "只跑方案"}
									</Button>
									<Button
										type="button"
										variant="outline"
										onClick={onToolRun}
										disabled={stageRunning !== null || !customerMessage.trim()}
									>
										{stageRunning === "tools" ? "工具运行中..." : "只跑工具"}
									</Button>
									<Button
										type="button"
										variant="outline"
										onClick={onKnowledgeRun}
										disabled={stageRunning !== null || !customerMessage.trim()}
									>
										{stageRunning === "knowledge" ? "知识运行中..." : "只跑知识"}
									</Button>
									<Button
										type="button"
										variant="outline"
										onClick={onPromptRun}
										disabled={stageRunning !== null || !customerMessage.trim()}
									>
										{stageRunning === "prompt" ? "回复生成运行中..." : "运行回复生成"}
									</Button>
									<Button
										type="button"
										variant="outline"
										onClick={onQualityRun}
										disabled={stageRunning !== null || !customerMessage.trim()}
									>
										{stageRunning === "quality" ? "质量策略运行中..." : "运行质量策略"}
									</Button>
								</div>
							</details>
						</div>
					</div>
				</div>

				{activeInspectorStage ? (
					<TraceInspectorPanel
						stage={activeInspectorStage}
						completeDisclosure
						testId="stage-inspector-panel"
						promptSnapshotEvent={
							activeRunEvent?.event_type === "generation_completed" ? activeRunEvent : null
						}
						onOpenPromptSnapshot={() => {
							if (activeRunEvent?.event_type === "generation_completed") {
								setPromptSnapshotEvent(activeRunEvent);
							}
						}}
					/>
				) : null}
			</div>

			<Dialog open={boundaryDialogOpen} onOpenChange={setBoundaryDialogOpen}>
				<DialogContent className="max-w-2xl">
					<DialogHeader>
						<DialogTitle>运行边界详情</DialogTitle>
						<DialogDescription>
							这些状态说明本次结果能用于哪类验收；它们不会改变客户对话主线，但排障时必须看。
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-3">
						{DEPENDENCY_ROWS.map((row) => (
							<div
								key={row.name}
								className="rounded-md border border-surface-border bg-surface-subtle px-3 py-3"
							>
								<div className="flex items-center justify-between gap-3">
									<div className="font-medium text-text-primary">{row.name}</div>
									<StatusPill tone={row.tone}>{row.state}</StatusPill>
								</div>
								<p className="mt-2 text-sm leading-6 text-text-secondary">{row.detail}</p>
							</div>
						))}
					</div>
				</DialogContent>
			</Dialog>
			<Dialog
				open={Boolean(promptSnapshotEvent)}
				onOpenChange={(open) => {
					if (!open) setPromptSnapshotEvent(null);
				}}
			>
				<DialogContent className="max-h-[84vh] max-w-5xl overflow-y-auto">
					<DialogHeader>
						<DialogTitle>主提示词快照</DialogTitle>
						<DialogDescription>
							这里用于核对主 Agent 生成阶段实际消费了哪些输入、变量和 Prompt
							版本；后端未返回时必须直接暴露契约缺口。
						</DialogDescription>
					</DialogHeader>
					{promptSnapshotEvent ? (
						<PromptSnapshotContent event={promptSnapshotEvent} result={result} />
					) : null}
				</DialogContent>
			</Dialog>
		</section>
	);
}

function RunEventTimeline({
	events,
	activeEventId,
	result,
	onSelectEvent,
}: {
	events: PipelineAdminRunEvent[];
	activeEventId: string | null;
	result: PipelineMessageResponse | null;
	onSelectEvent: (event: PipelineAdminRunEvent) => void;
}) {
	return (
		<div className="mt-3 space-y-2" data-testid="run-event-timeline">
			{events.map((event, index) => {
				const state = runEventStatus(event.status);
				const chips = runEventChips(event, result);
				const isActive = event.event_id === activeEventId;
				const timingLabel = runEventTimingLabel(event);
				return (
					<button
						key={event.event_id}
						type="button"
						data-testid={`run-event-${event.event_type}`}
						onClick={() => onSelectEvent(event)}
						className={`grid w-full items-start gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-left transition md:grid-cols-[28px_minmax(0,1fr)_auto_auto] ${
							isActive
								? "border-brand-accent bg-brand-accent/10 shadow-sm"
								: "border-surface-border bg-surface-background hover:bg-surface-subtle"
						}`}
					>
						<span className="flex h-6 w-6 items-center justify-center rounded-full border border-surface-border bg-surface-card text-xs font-semibold text-text-secondary">
							{index + 1}
						</span>
						<span className="min-w-0">
							<span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold text-text-primary">
								{event.title}
								<span className="font-mono text-xs font-normal text-text-muted">
									{event.event_type}
								</span>
							</span>
							<span className="mt-1 block text-xs leading-5 text-text-secondary">
								{event.summary || "后端事件未返回说明。"}
							</span>
							{chips.length > 0 ? (
								<span className="mt-1.5 flex flex-wrap gap-1.5">
									{chips.map((chip) => (
										<span
											key={`${event.event_id}-${chip}`}
											className="rounded border border-surface-border bg-surface-card px-2 py-0.5 text-xs text-text-secondary"
										>
											{chip}
										</span>
									))}
								</span>
							) : null}
						</span>
						<span
							className="inline-flex h-6 w-fit max-w-max items-center justify-self-start rounded-full border border-surface-border bg-surface-card px-2 text-[11px] font-medium leading-none text-text-muted md:justify-self-end md:self-start"
							aria-label={`时间标签 ${timingLabel}`}
						>
							{timingLabel}
						</span>
						<span className="md:justify-self-end md:self-start">
							<RunDetailStatusPill tone={state.tone}>{state.status}</RunDetailStatusPill>
						</span>
					</button>
				);
			})}
		</div>
	);
}

function PromptSnapshotContent({
	event,
	result,
}: {
	event: PipelineAdminRunEvent;
	result: PipelineMessageResponse | null;
}) {
	const snapshot = asRecord(event.raw.prompt_snapshot);
	if (!snapshot) {
		return (
			<div className="space-y-3">
				<div className="rounded-md border border-brand-warning/30 bg-brand-warning/10 p-3 text-sm leading-6 text-brand-warning">
					当前 AdminRunEvent 未返回 prompt_snapshot。前端只能展示生成事件、MessagesResponse 与
					phase4 摘要，不能假装已经拿到完整 Prompt 输入。
				</div>
				<div className="grid gap-2 text-sm md:grid-cols-2">
					<ResultMetric
						label="生成事件"
						value={`${event.title} · ${eventStatusLabel(event.status)}`}
					/>
					<ResultMetric
						label="Prompt 状态"
						value={
							result
								? promptEffectDisplay(result.runtime_capabilities.prompt_effect_mode).value
								: "未返回"
						}
					/>
					<ResultMetric
						label="生成模式"
						value={
							result ? runnerModeLabel(result.runtime_capabilities.phase4_runner_mode) : "未返回"
						}
					/>
					<ResultMetric label="回复 trace" value={event.trace_id ?? result?.trace_id ?? "未返回"} />
				</div>
				<pre className="max-h-80 overflow-auto rounded-md bg-surface-subtle p-3 text-xs leading-5 text-text-secondary">
					{formatJson({ event, messages_response: result })}
				</pre>
			</div>
		);
	}

	const injectedVariables = asRecord(snapshot.injected_variables) ?? {};
	const playbook = asRecord(injectedVariables.playbook) ?? {};
	const toolRuntime = asRecord(injectedVariables.tool_runtime_context) ?? {};
	// 0527 T5：结构化语义（实体/检索词/命中分支）——主 Agent 调 Skill 时实际拿到的上下文
	const structuredSemantics = asRecord(injectedVariables.structured_semantics) ?? {};
	const modelOutput = asRecord(snapshot.model_output) ?? {};
	const promptEffect = promptEffectDisplay(
		snapshot.prompt_effect_mode as PipelineRuntimeCapabilities["prompt_effect_mode"],
	);
	const runnerMode = runnerModeLabel(
		snapshot.runner_mode as PipelineRuntimeCapabilities["phase4_runner_mode"],
	);

	return (
		<div className="space-y-4" data-testid="prompt-snapshot-detail">
			<div className="grid gap-2 text-sm md:grid-cols-4">
				<ResultMetric label="Prompt 版本" value={stringifyScalar(snapshot.prompt_version)} />
				<ResultMetric label="Prompt 状态" value={promptEffect.value} />
				<ResultMetric label="生成模式" value={runnerMode} />
				<ResultMetric
					label="Prompt tokens"
					value={stringifyScalar(snapshot.rendered_prompt_tokens)}
				/>
			</div>

			<PromptSnapshotSection
				title="注入变量"
				description="这些是主 Agent 生成本轮回复前实际拿到的业务上下文。"
			>
				<div className="grid gap-2 text-sm md:grid-cols-2">
					<ResultMetric
						label="客户本轮问题"
						value={stringifyScalar(injectedVariables.current_user_message)}
					/>
					<ResultMetric
						label="语义上下文"
						value={truncateForMetric(injectedVariables.semantic_context)}
					/>
					<ResultMetric
						label="处理路径"
						value={`${stringifyScalar(playbook.id)} · ${stringifyScalar(playbook.name)}`}
					/>
					<ResultMetric
						label="允许技能"
						value={stringifyScalar(injectedVariables.allowed_skills)}
					/>
					<ResultMetric label="工具/知识证据" value={stringifyScalar(toolRuntime.evidence_refs)} />
					<ResultMetric label="步骤预算" value={stringifyScalar(injectedVariables.step_budget)} />
					<ResultMetric
						label="语义任务"
						value={stringifyScalar(structuredSemantics.semantic_task)}
					/>
					<ResultMetric
						label="实体/检索词"
						value={truncateForMetric(structuredSemantics.semantic_entities)}
					/>
					<ResultMetric
						label="命中分支"
						value={stringifyScalar(structuredSemantics.selected_branch)}
					/>
					<ResultMetric
						label="证据需求"
						value={stringifyScalar(structuredSemantics.evidence_needs)}
					/>
				</div>
				<pre className="mt-3 max-h-60 overflow-auto rounded-md bg-surface-subtle p-3 text-xs leading-5 text-text-secondary">
					{formatJson(injectedVariables)}
				</pre>
			</PromptSnapshotSection>

			<PromptSnapshotSection
				title="渲染 Prompt"
				description="这是发送给主 Agent 的系统提示词渲染结果。"
			>
				<pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-md bg-surface-subtle p-3 text-xs leading-5 text-text-secondary">
					{stringifyScalar(snapshot.rendered_prompt)}
				</pre>
			</PromptSnapshotSection>

			<PromptSnapshotSection title="模型输出" description="这是主 Agent 输出被解析后的结构化结果。">
				<pre className="max-h-72 overflow-auto rounded-md bg-surface-subtle p-3 text-xs leading-5 text-text-secondary">
					{formatJson(modelOutput)}
				</pre>
			</PromptSnapshotSection>

			<PromptSnapshotSection
				title="对客最终回复"
				description="这是经过主链路和质检后返回给客户的文本。"
			>
				<div className="rounded-md border border-surface-border bg-surface-background p-3 text-sm leading-6 text-text-primary">
					{stringifyScalar(snapshot.final_reply)}
				</div>
			</PromptSnapshotSection>

			<PromptSnapshotSection
				title="原始 JSON"
				description="研发排障时使用，和后端 raw.prompt_snapshot 保持一致。"
			>
				<pre className="max-h-96 overflow-auto rounded-md bg-surface-subtle p-3 text-xs leading-5 text-text-secondary">
					{formatJson(snapshot)}
				</pre>
			</PromptSnapshotSection>
		</div>
	);
}

function PromptSnapshotSection({
	title,
	description,
	children,
}: {
	title: string;
	description: string;
	children: ReactNode;
}) {
	return (
		<section className="rounded-md border border-surface-border bg-surface-background p-4">
			<div className="mb-3">
				<h4 className="text-sm font-semibold text-text-primary">{title}</h4>
				<p className="mt-1 text-xs leading-5 text-text-muted">{description}</p>
			</div>
			{children}
		</section>
	);
}

function ChatBubble({
	speaker,
	label,
	content,
	emphasized = false,
}: {
	speaker: ContextRole;
	label: string;
	content: string;
	emphasized?: boolean;
}) {
	const isUser = speaker === "user";
	return (
		<div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
			<div
				className={`max-w-[84%] rounded-md border px-4 py-3 ${
					isUser
						? "border-brand-primary bg-brand-primary text-text-inverse"
						: "border-surface-border bg-surface-card text-text-primary"
				} ${emphasized ? "shadow-sm" : ""}`}
			>
				<div
					className={`text-xs font-medium ${isUser ? "text-text-inverse/75" : "text-text-muted"}`}
				>
					{label}
				</div>
				<p className="mt-1 text-sm leading-6">{content}</p>
			</div>
		</div>
	);
}

function TraceInspectorPanel<TId extends string>({
	stage,
	completeDisclosure,
	testId,
	promptSnapshotEvent,
	onOpenPromptSnapshot,
}: {
	stage: RunDetailStage<TId>;
	completeDisclosure: boolean;
	testId: string;
	promptSnapshotEvent?: PipelineAdminRunEvent | null;
	onOpenPromptSnapshot?: () => void;
}) {
	const [showRaw, setShowRaw] = useState(false);
	const visibleFields = completeDisclosure ? stage.primaryFields : stage.primaryFields.slice(0, 7);

	return (
		<aside
			className="sticky top-4 h-fit rounded-md border border-surface-border bg-surface-card p-4"
			data-testid={testId}
		>
			<div className="flex items-start justify-between gap-3">
				<div>
					<div className="text-xs font-medium text-text-muted">右侧明细</div>
					<h3 className="mt-1 text-base font-semibold text-text-primary">
						当前选中：{stage.title}
					</h3>
				</div>
				<RunDetailStatusPill tone={stage.tone}>{stage.status}</RunDetailStatusPill>
			</div>
			<p className="mt-3 text-sm leading-6 text-text-secondary">{stage.summary}</p>
			<div className="mt-4 space-y-2">
				{visibleFields.map((field) => (
					<div
						key={`${stage.id}-${field.rawName}-${field.label}`}
						className="rounded-md border border-surface-border bg-surface-subtle px-3 py-2"
					>
						<div className="flex items-start justify-between gap-3">
							<div className="text-sm font-medium text-text-primary">{field.label}</div>
							<div className="text-right text-sm text-text-secondary">{field.value}</div>
						</div>
						<div className="mt-1 text-xs text-text-muted">
							原始字段：{field.rawName} · 来源：{field.source}
						</div>
					</div>
				))}
			</div>
			<div className="mt-4 flex flex-wrap gap-2">
				<Button
					type="button"
					variant="outline"
					size="sm"
					onClick={() => setShowRaw((value) => !value)}
				>
					查看原始 JSON
				</Button>
				{promptSnapshotEvent ? (
					<Button type="button" variant="outline" size="sm" onClick={onOpenPromptSnapshot}>
						查看主提示词快照
					</Button>
				) : null}
				{!completeDisclosure ? (
					<span className="text-xs leading-9 text-text-muted">完整披露会显示全部字段。</span>
				) : null}
			</div>
			{showRaw ? (
				<pre className="mt-3 max-h-80 overflow-auto rounded-md bg-surface-subtle p-3 text-xs leading-5 text-text-secondary">
					{formatJson(stage.raw)}
				</pre>
			) : null}
		</aside>
	);
}

function understandingStageStatusFor(
	result: PipelineMessageResponse | null,
	phase2: PipelineMessageResponse["phase_trace"][number] | undefined,
): StepStatus {
	if (!result) return "跳过";
	if (result.intent.intent === "unknown" && !summaryText(phase2?.summary, "semantic_task", "")) {
		return "受限";
	}
	if (result.match_mode === "no_match") return "受限";
	return "通过";
}

function understandingStageToneFor(
	result: PipelineMessageResponse | null,
	phase2: PipelineMessageResponse["phase_trace"][number] | undefined,
): StepTone {
	if (!result) return "neutral";
	if (result.intent.intent === "unknown" && !summaryText(phase2?.summary, "semantic_task", "")) {
		return "warning";
	}
	if (result.match_mode === "no_match") return "neutral";
	return "success";
}

function evidenceStageStatusFor(
	result: PipelineMessageResponse | null,
	phase2: PipelineMessageResponse["phase_trace"][number] | undefined,
): Pick<RunFlowStage, "status" | "tone"> {
	if (!result) return { status: "跳过", tone: "neutral" };
	if (result.runtime_capabilities.rag_mode === "error") return { status: "受限", tone: "blocked" };
	if (result.runtime_capabilities.shopify_read_mode === "failed_or_blocked") {
		return { status: "受限", tone: "blocked" };
	}
	const needsEvidence = Array.isArray(phase2?.summary.evidence_needs)
		? phase2.summary.evidence_needs.length > 0
		: false;
	const skillResultCount = summaryNumber(phase2?.summary.skill_result_count) ?? 0;
	const ragSnippetCount = summaryNumber(phase2?.summary.rag_snippet_count) ?? 0;
	const hasEvidence = skillResultCount > 0 || ragSnippetCount > 0;
	if (needsEvidence && !hasEvidence) return { status: "受限", tone: "warning" };
	return hasEvidence ? { status: "通过", tone: "success" } : { status: "跳过", tone: "neutral" };
}

function replyStageStatusFor(
	result: PipelineMessageResponse | null,
): Pick<RunFlowStage, "status" | "tone"> {
	if (!result) return { status: "跳过", tone: "neutral" };
	if (result.final_outcome === "failed" || result.final_outcome === "handoff") {
		return { status: "受限", tone: "blocked" };
	}
	if (
		result.final_action_type === "clarify" ||
		result.runtime_capabilities.phase4_runner_mode === "static"
	) {
		return { status: "受限", tone: "warning" };
	}
	return { status: "通过", tone: "success" };
}

function shortId(value: string): string {
	return value.length > 12 ? `${value.slice(0, 8)}...` : value;
}

function numericTiming(value: number | null | undefined): number | null {
	return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

function formatDurationMs(value: number | null | undefined): string {
	const ms = numericTiming(value);
	if (ms === null) return "未返回";
	if (ms < 1000) return `${ms}ms`;
	return `${(ms / 1000).toFixed(2)}s`;
}

function totalLatencyFromEvents(events: PipelineAdminRunEvent[]): number | null {
	const completed = events.find((event) => event.event_type === "run_completed");
	return numericTiming(completed?.duration_ms ?? completed?.offset_ms);
}

function latestEventOffsetMs(events: PipelineAdminRunEvent[]): number | null {
	for (let index = events.length - 1; index >= 0; index -= 1) {
		const value = numericTiming(events[index]?.offset_ms);
		if (value !== null) return value;
	}
	return null;
}

function runEventTimingLabel(event: PipelineAdminRunEvent): string {
	const durationMs = numericTiming(event.duration_ms);
	const offsetMs = numericTiming(event.offset_ms);
	if (event.event_type === "run_completed" && durationMs !== null) {
		return `总 ${formatDurationMs(durationMs)}`;
	}
	if (event.timing_source === "phase_trace" || event.timing_source === "trace_replay") {
		return durationMs !== null ? formatDurationMs(durationMs) : "未返回";
	}
	if (durationMs !== null) return formatDurationMs(durationMs);
	if (offsetMs !== null) return `+${formatDurationMs(offsetMs)}`;
	return "未返回";
}

function timingSourceLabel(source: PipelineAdminRunEvent["timing_source"]): string {
	if (source === "progress_clock") return "实时偏移";
	if (source === "phase_trace") return "Phase 真实耗时";
	if (source === "trace_total") return "总耗时";
	if (source === "trace_replay") return "Trace 回放耗时";
	return "未返回耗时";
}

function timingSourceHelp(event: PipelineAdminRunEvent): string {
	if (event.timing_source === "progress_clock") {
		return "由服务端运行时钟记录，表示事件距本轮开始的真实偏移。";
	}
	if (event.timing_source === "phase_trace") {
		if (
			[
				"semantic_frame_completed",
				"playbook_recalled",
				"evidence_fetch_started",
				"tool_completed",
				"rag_completed",
			].includes(event.event_type)
		) {
			return "当前理解与证据阶段内部尚未拆工具级计时，因此这里展示所属阶段的真实耗时。";
		}
		return "由 AgentOrchestrator PhaseTraceEntry.elapsed_ms 记录。";
	}
	if (event.timing_source === "trace_total") {
		return "整轮运行总耗时，当前运行由 Admin 服务端测量，历史回放来自 Trace.total_latency_ms。";
	}
	if (event.timing_source === "trace_replay") {
		return "历史 Trace 可还原阶段耗时，但没有实时流式 offset。";
	}
	return "本事件当前没有可用的真实时间字段。";
}

function slowestPhaseLabel(result: PipelineMessageResponse | null): string {
	if (!result || result.phase_trace.length === 0) return "待运行";
	const slowest = result.phase_trace.reduce((current, next) =>
		next.elapsed_ms > current.elapsed_ms ? next : current,
	);
	return `${phaseDisplayName(slowest.phase)} ${formatDurationMs(slowest.elapsed_ms)}`;
}

function phaseDisplayName(phase: string): string {
	const labels: Record<string, string> = {
		phase1_refine: "问题整理",
		phase2_recall: "理解与证据",
		phase3_rerank: "证据整理",
		phase4_generate: "回复生成",
		phase5_validate: "质量检查",
	};
	return labels[phase] ?? phase;
}

function finalActionLabel(
	action: PipelineMessageResponse["final_action_type"],
	outcome: PipelineMessageResponse["final_outcome"],
): string {
	if (action === "handoff" || outcome === "handoff") return "转人工预留";
	if (action === "clarify") return "已追问";
	if (action === "commit_action") return "执行受控动作";
	if (outcome === "failed") return "失败";
	return "已回答";
}

function runnerModeLabel(mode: PipelineRuntimeCapabilities["phase4_runner_mode"]): string {
	return phase4RunnerDisplay(mode).value;
}

function loopExecutionLabel(
	output: Record<string, unknown> | null | undefined,
	phase4: Record<string, unknown> | null | undefined,
): string {
	const meta = asRecord(output?.loop_meta) ?? asRecord(phase4?.loop_meta);
	const count =
		summaryNumber(output?.executed_action_count) ??
		summaryNumber(phase4?.executed_action_count) ??
		0;
	if (!meta) return count > 0 ? `已记录 ${count} 次工具调用` : "未启用";
	const steps = summaryNumber(meta.steps_used) ?? 0;
	const exhausted = meta.budget_exhausted === true ? "预算已耗尽" : "预算未耗尽";
	const hitRate = summaryNumber(meta.dedup_hit_rate);
	const hitRateLabel = hitRate === null ? "未返回" : `${Math.round(hitRate * 100)}%`;
	const rawActions = Array.isArray(output?.executed_actions)
		? output.executed_actions
		: Array.isArray(phase4?.executed_actions)
			? phase4.executed_actions
			: [];
	const actions = rawActions as unknown[];
	const wrappers = actions
		.map((item) => asRecord(item))
		.filter((item): item is Record<string, unknown> => item !== null)
		.map((item) => recordString(item, "function_name") || recordString(item, "skill_id"))
		.filter((item) => item.length > 0)
		.slice(0, 3);
	const wrapperLabel = wrappers.length > 0 ? `；${wrappers.join(", ")}` : "";
	return `调用 ${count} 次；steps=${steps}；${exhausted}；缓存命中 ${hitRateLabel}${wrapperLabel}`;
}

function buildRuntimeDiagnosticItems(
	result: PipelineMessageResponse | null,
): RuntimeDiagnosticItem[] {
	const phase4Entry = result?.phase_trace.find((entry) => entry.phase === "phase4_generate");
	const phase5Entry = result?.phase_trace.find((entry) => entry.phase === "phase5_validate");
	const phase4 = phase4Entry?.summary;
	const phase5 = phase5Entry?.summary;
	return [
		{ label: "主 Agent 回退", value: runnerErrorLabel(phase4) },
		{ label: "工具 loop", value: loopExecutionLabel(null, phase4) },
		{ label: "Step 使用", value: stepUsageLabel(null, phase5) },
		{
			label: "Grounding 检查",
			value: groundingCheckLabel(asRecord(phase5?.grounding_check)),
		},
		{
			label: "Side-effect 检查",
			value: sideEffectCheckLabel(asRecord(phase5?.side_effect_check)),
		},
		{
			label: "终止原因",
			value: terminatedByLabel(
				summaryText(phase5, "terminated_by", phase4Entry?.status ?? "未返回"),
			),
		},
	];
}

function runnerErrorLabel(summary: Record<string, unknown> | null | undefined): string {
	const value = summaryText(summary, "runner_error", "无");
	return value.trim().length > 0 ? value : "无";
}

function stepUsageLabel(
	primary: Record<string, unknown> | null | undefined,
	fallback: Record<string, unknown> | null | undefined,
): string {
	const used = summaryNumber(primary?.step_used) ?? summaryNumber(fallback?.step_used);
	const budget = summaryNumber(primary?.step_budget) ?? summaryNumber(fallback?.step_budget);
	const terminatedBy = summaryText(
		primary,
		"terminated_by",
		summaryText(fallback, "terminated_by", "未返回"),
	);
	if (used === null && budget === null) return "未返回";
	const usedLabel = used === null ? "未返回" : String(used);
	const budgetLabel = budget === null ? "未返回" : String(budget);
	return `${usedLabel} / ${budgetLabel}；终止=${terminatedByLabel(terminatedBy)}`;
}

function groundingCheckLabel(check: Record<string, unknown> | null): string {
	if (!check) return "未返回";
	if (check.triggered !== true) return "未触发";
	const status = checkPassedLabel(check.passed);
	const ratio = summaryNumber(check.grounded_ratio);
	const ratioLabel = ratio === null ? "覆盖未返回" : `覆盖 ${Math.round(ratio * 100)}%`;
	const unsupported = stringListFromUnknown(check.unsupported_claims);
	if (check.passed === false) {
		return `${status} · ${unsupported.length > 0 ? unsupported.join("、") : "存在未支撑表述"}`;
	}
	return `${status} · ${ratioLabel}`;
}

function sideEffectCheckLabel(check: Record<string, unknown> | null): string {
	if (!check) return "未返回";
	if (check.triggered !== true) return "未触发";
	const issues = stringListFromUnknown(check.issues);
	if (check.passed === false) {
		return `未通过 · ${issues.length > 0 ? issues.join("、") : "存在副作用风险"}`;
	}
	if (check.passed === true) return "通过 · 无副作用风险";
	return "未判定";
}

function checkPassedLabel(value: unknown): string {
	if (value === true) return "通过";
	if (value === false) return "未通过";
	return "未判定";
}

function stringListFromUnknown(value: unknown): string[] {
	if (!Array.isArray(value)) return [];
	return value.map((item) => stringifyScalar(item)).filter((item) => item.length > 0);
}

function terminatedByLabel(value: string): string {
	if (value === "natural") return "自然结束";
	if (value === "step_budget") return "Step 预算耗尽";
	if (value === "safety_block") return "安全阻断";
	if (value === "runner_error") return "运行器异常";
	return value || "未返回";
}

function confirmationPlanLabel(
	output: Record<string, unknown> | null | undefined,
	phase4: Record<string, unknown> | null | undefined,
): string {
	const plan = asRecord(output?.confirmation_plan) ?? asRecord(phase4?.confirmation_plan);
	if (!plan) return "无确认计划";
	const status = String(
		output?.confirmation_status ?? phase4?.confirmation_status ?? "pending_user_confirmation",
	);
	const skillId = String(plan.skill_id ?? "未知 Skill");
	const toolId = String(plan.tool_id ?? "未知工具");
	const auditId = String(plan.audit_id ?? phase4?.write_audit_id ?? "");
	const auditStatus = String(phase4?.write_audit_status ?? "");
	const writeMode =
		plan.write_mode === "plan_only" ? "plan-only" : String(plan.write_mode ?? "未知模式");
	const mutationLabel = plan.mutation_executed === false ? "不执行真实写入" : "写入状态待核验";
	const auditLabel = auditId ? `；审计 ${auditId}${auditStatus ? `(${auditStatus})` : ""}` : "";
	return `${confirmationStatusLabel(status)}；${skillId} / ${toolId}；${writeMode}；${mutationLabel}${auditLabel}`;
}

function confirmationStatusLabel(status: string): string {
	if (status === "pending_user_confirmation") return "等待用户确认";
	if (status === "confirmed") return "已确认";
	if (status === "rejected") return "已拒绝";
	if (status === "expired") return "已过期";
	if (status === "not_required") return "无需确认";
	return status || "等待用户确认";
}

function matchModeLabel(value: PipelineMessageResponse["match_mode"] | undefined): string {
	if (!value) return "未运行";
	if (value === "routing_table") return "RoutingTable 命中";
	if (value === "short_circuit") return "短路径命中";
	if (value === "config_mapping") return "兜底映射命中";
	if (value === "vector") return "语义兜底命中";
	if (value === "no_match") return "未命中处理路径";
	return "延续上轮方案";
}

function routingTargetLabel(
	result: Pick<PipelineMessageResponse, "recommended_playbook" | "short_circuit_skill">,
): string {
	if (result.short_circuit_skill) return skillLabel(result.short_circuit_skill);
	return productPlaybookLabel(result.recommended_playbook?.id ?? "无方案命中");
}

type Phase2SummaryLike = PipelinePhaseTraceEntry | Record<string, unknown> | null | undefined;

function phase2SummaryRecord(phase2: Phase2SummaryLike): Record<string, unknown> | null {
	if (!phase2) return null;
	if ("summary" in phase2 && typeof phase2.summary === "object" && phase2.summary !== null) {
		return phase2.summary as Record<string, unknown>;
	}
	return phase2 as Record<string, unknown>;
}

function branchSourceLabel(value: string): string {
	if (value === "routing_table") return "RoutingTable";
	if (value === "sub_task") return "子任务";
	if (value === "confidence_gate") return "低置信度保守";
	return value || "未返回";
}

function runtimeBranchLabel(
	result: Pick<
		PipelineMessageResponse,
		"selected_branch" | "selected_branch_display_name" | "selected_branch_source"
	> | null,
	phase2: Phase2SummaryLike,
): string {
	const summary = phase2SummaryRecord(phase2);
	const displayName =
		summaryText(summary, "selected_branch_display_name", "") ||
		result?.selected_branch_display_name ||
		"";
	const branch = summaryText(summary, "selected_branch", "") || result?.selected_branch || "";
	const source =
		summaryText(summary, "selected_branch_source", "") || result?.selected_branch_source || "";
	const confidenceGateHit = summary?.confidence_gate_hit === true;
	if (displayName || branch) {
		const sourceSuffix = source ? ` · ${branchSourceLabel(source)}` : "";
		const branchSuffix = branch && branch !== displayName ? `（${branch}${sourceSuffix}）` : "";
		return `${displayName || branch}${branchSuffix}`;
	}
	return confidenceGateHit ? "低置信度，沿用 PB 默认" : "PB 默认";
}

function phaseAllowedSkillsLabel(phase2: Phase2SummaryLike): string {
	const summary = phase2SummaryRecord(phase2);
	const allowed = summary?.allowed_skills;
	if (Array.isArray(allowed) && allowed.length > 0) {
		return allowed.map((item) => String(item)).join(",");
	}
	return "未返回";
}

function skillDecisionPlanLabel(value: unknown): string {
	if (!Array.isArray(value) || value.length === 0) return "未返回";
	const labels = value
		.map((item) => asRecord(item))
		.filter((item): item is Record<string, unknown> => item !== null)
		.map((decision) => {
			const skillId = recordString(decision, "skill_id") || "Skill";
			const status = skillDecisionStatusLabel(recordString(decision, "status"));
			const runMode = skillDecisionRunModeLabel(recordString(decision, "run_mode"));
			const toolId = recordString(decision, "selected_tool_id");
			const blocked = recordString(decision, "blocked_reason");
			const missing = stringListFromRecord(decision, "missing_slots");
			const plan = asRecord(decision.tool_plan);
			const args = asRecord(plan?.args);
			const argsPreview = args
				? Object.entries(args)
						.filter(([key]) => key !== "merchant_id")
						.slice(0, 3)
						.map(([key, argValue]) => `${key}=${stringifyScalar(argValue)}`)
						.join(", ")
				: "";
			const details =
				blocked ||
				(missing.length > 0 ? `缺 ${missing.join("/")}` : "") ||
				(toolId ? `${toolId}${argsPreview ? `(${argsPreview})` : ""}` : "");
			return `${skillLabel(skillId)}：${status}${runMode ? ` · ${runMode}` : ""}${details ? ` · ${details}` : ""}`;
		});
	return labels.length > 0 ? labels.join("；") : "未返回";
}

function recordString(record: Record<string, unknown>, key: string): string {
	const value = record[key];
	return typeof value === "string" ? value : "";
}

function stringListFromRecord(record: Record<string, unknown>, key: string): string[] {
	const value = record[key];
	if (!Array.isArray(value)) return [];
	return value.map((item) => String(item)).filter((item) => item.length > 0);
}

function skillDecisionStatusLabel(status: string): string {
	if (status === "planned") return "已计划";
	if (status === "clarify") return "需补充";
	if (status === "blocked") return "被限制";
	if (status === "not_applicable") return "未启用";
	return status || "未返回";
}

function skillDecisionRunModeLabel(mode: string): string {
	if (mode === "live") return "真实只读";
	if (mode === "shadow") return "仅观察";
	if (mode === "dry_run") return "试跑";
	return "";
}

function semanticTaskLabel(value: string): string {
	if (!value || value === "无") return "未返回";
	const labels: Record<string, string> = {
		greeting: "问候承接",
		list_products: "了解店铺商品",
		knowledge_answer: "咨询政策/知识",
		refund_or_return_request: "退款/退货处理",
		order_lookup: "查询订单/物流",
		address_change_request: "修改收货地址",
		general_clarification: "通用澄清",
	};
	return labels[value] ?? value;
}

function StatusPill({
	children,
	tone,
}: {
	children: string;
	tone: "success" | "warning" | "neutral" | "blocked";
}) {
	const toneClass =
		tone === "success"
			? "border-brand-success/30 bg-brand-success/10 text-brand-success"
			: tone === "warning"
				? "border-brand-warning/30 bg-brand-warning/10 text-brand-warning"
				: tone === "blocked"
					? "border-brand-danger/30 bg-brand-danger/10 text-brand-danger"
					: "border-surface-border bg-surface-subtle text-text-secondary";

	return (
		<span className={`inline-flex rounded-md border px-2 py-1 text-xs font-medium ${toneClass}`}>
			{children}
		</span>
	);
}

function dataModeTone(mode: DataMode): "success" | "warning" | "neutral" | "blocked" {
	if (mode === "real_read") return "success";
	if (mode === "real_write") return "warning";
	if (mode === "sandbox") return "neutral";
	return "blocked";
}

function EmptyRunState() {
	return (
		<section className="rounded-md border border-dashed border-surface-border bg-surface-card p-8 text-center">
			<ClipboardCheck className="mx-auto h-8 w-8 text-brand-primary" aria-hidden="true" />
			<h3 className="mt-3 text-base font-semibold text-text-primary">等待运行</h3>
			<p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-text-secondary">
				点击「运行全链路」后，这里会展示最终回复、证据引用、动作结果和审计记录。当前页面用于验证应答主链路，真实订单和真实知识检索仍按后续接入。
			</p>
		</section>
	);
}

function StageRunResult({
	mode,
	result,
	error,
	running,
}: {
	mode: DataMode;
	result: StageResult | null;
	error: string | null;
	running: StageKind | null;
}) {
	if (!result && !error && !running) return null;
	let title = "单环节运行失败";
	if (running) {
		title = stageRunningTitle(running);
	} else if (result) {
		title = result.title;
	}

	return (
		<section
			className="rounded-md border border-surface-border bg-surface-card p-4"
			data-testid="pipeline-stage-result"
		>
			<div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
				<div>
					<h3 className="text-base font-semibold text-text-primary">{title}</h3>
					<p className="mt-1 text-sm text-text-secondary">
						单环节按钮会真实调用对应接口或复用应答运行事件；失败会保留错误信息，不能当作通过。
					</p>
				</div>
				<StatusPill
					tone={
						error || result?.status === "error"
							? "blocked"
							: result?.status === "partial"
								? "warning"
								: "success"
					}
				>
					{running
						? "运行中"
						: error || result?.status === "error"
							? "失败"
							: result?.status === "partial"
								? "部分通过"
								: "通过"}
				</StatusPill>
			</div>
			{error ? (
				<div className="mt-3 rounded-md border border-brand-danger/30 bg-brand-danger/10 p-3 text-sm text-brand-danger">
					{error}
				</div>
			) : null}
			{result ? (
				<div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
					{result.summary.map((item) => (
						<ResultMetric
							key={`${result.kind}-${item.label}`}
							label={item.label}
							value={item.value}
						/>
					))}
				</div>
			) : null}
			{result ? <SingleStageResultTableSync mode={mode} result={result} /> : null}
			{result ? (
				<pre className="mt-4 max-h-72 overflow-auto rounded-md bg-surface-subtle p-3 text-xs leading-5 text-text-secondary">
					{formatJson(result.raw)}
				</pre>
			) : null}
		</section>
	);
}

function SingleStageResultTableSync({ mode, result }: { mode: DataMode; result: StageResult }) {
	const sync = buildSingleStageResultTableSync(result, { mock: mode === "mock" });

	return (
		<div
			className="mt-4 rounded-md border border-brand-info/30 bg-brand-info/10 p-3 text-sm text-brand-info"
			data-testid="single-stage-result-table-sync"
		>
			<div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
				<div>
					<div className="font-semibold text-brand-info">本次运行结果</div>
					<p className="mt-1 leading-6">
						{sync.stageLabel}：{sync.actualResult}；{sync.compareText}。
					</p>
				</div>
				<StatusPill tone={result.status === "success" ? "success" : "warning"}>
					{result.status === "success" ? "本次已生成" : "本次需复核"}
				</StatusPill>
			</div>
			<p className="mt-2 text-xs leading-5 text-brand-info">
				本次运行结果仅在页面会话展示，不写入评测运行，不自动生成问题。{sync.coverageGap}
			</p>
			<div className="mt-3 flex flex-col gap-2 rounded-md border border-brand-info/30 bg-white/70 px-3 py-2 text-xs leading-5 text-brand-info md:flex-row md:items-center md:justify-between">
				<span>失败可复现后，可以带入评测运营人工确认；不会自动创建问题。</span>
				<a
					href={sync.manualCandidateHref}
					className="inline-flex w-fit items-center gap-1 rounded-md border border-brand-info/30 bg-white px-2.5 py-1.5 font-medium text-brand-info hover:bg-brand-info/10"
				>
					带入评测运营人工候选
					<ArrowRight className="h-3 w-3" aria-hidden="true" />
				</a>
			</div>
		</div>
	);
}

function buildSingleStageResultTableSync(result: StageResult, options: { mock?: boolean } = {}) {
	if (result.kind === "intent") {
		return {
			stageLabel: "意图分类",
			actualResult: `actual_intent=${stageSummaryValue(result, "意图")}`,
			compareText: "可对比 expected_intent vs actual_intent",
			coverageGap: " 已可带入人工候选；自动提交评测运行仍后置。",
			manualCandidateHref: buildSingleStageManualCandidateHref({
				mock: options.mock,
				sampleId: "P10TA001",
				targetStage: "intent_classification",
			}),
		};
	}
	if (result.kind === "playbook") {
		return {
			stageLabel: "处理方案",
			actualResult: `actual_route=${stageSummaryValue(result, "处理路径")}`,
			compareText: "可对比 expected_pb vs actual_route",
			coverageGap: " 当前不在线编辑处理方案，也不自动改映射。",
			manualCandidateHref: buildSingleStageManualCandidateHref({
				mock: options.mock,
				sampleId: "P10TA001",
				targetStage: "playbook_routing",
			}),
		};
	}
	if (result.kind === "knowledge") {
		return {
			stageLabel: "知识与证据",
			actualResult: `knowledge_hits=${stageSummaryValue(result, "命中数")}`,
			compareText: "可对比 expected_evidence_refs vs 知识检索命中",
			coverageGap: " 当前只验证知识检索沙盒，不代表主链路一定消费了该证据。",
			manualCandidateHref: buildSingleStageManualCandidateHref({
				mock: options.mock,
				sampleId: "P10TA005",
				targetStage: "knowledge_retrieval",
			}),
		};
	}
	if (result.kind === "prompt") {
		return {
			stageLabel: "提示词与话术",
			actualResult: `final_answer=${stageSummaryValue(result, "最终回复")}`,
			compareText: "可对比 expected_prompt_checks vs prompt_snapshot/evidence_refs/final_answer",
			coverageGap: " 当前复用运行生成事件，不代表主 Agent 自主工具循环已开启。",
			manualCandidateHref: buildSingleStageManualCandidateHref({
				mock: options.mock,
				sampleId: "P10TA001",
				targetStage: "prompt_response",
			}),
		};
	}
	if (result.kind === "quality") {
		return {
			stageLabel: "质量策略",
			actualResult: `quality_decision=${stageSummaryValue(result, "质量判定")}`,
			compareText: "可对比 expected_quality_decision vs quality_completed.output.decision",
			coverageGap: " 当前复用运行质量事件，不代表独立质量判断服务已上线。",
			manualCandidateHref: buildSingleStageManualCandidateHref({
				mock: options.mock,
				sampleId: "P10TA007",
				targetStage: "quality_guard",
			}),
		};
	}

	const knowledgeSummary = stageSummaryValue(result, "知识检索");
	const knowledgeHits =
		knowledgeSummary.match(/hits=(\d+)/)?.[1] ??
		knowledgeSummary.match(/命中\s*(\d+)\s*条/)?.[1] ??
		"未返回";
	return {
		stageLabel: "工具与证据",
		actualResult: `knowledge_hits=${knowledgeHits}`,
		compareText: "可查看授权、知识检索沙盒和知识命中结果",
		coverageGap: " 工具环节当前还未按每条样本动态选择订单、商品等全部能力。",
		manualCandidateHref: buildSingleStageManualCandidateHref({
			mock: options.mock,
			sampleId: "P10TA005",
			targetStage: "tool_and_evidence",
		}),
	};
}

function buildSingleStageManualCandidateHref({
	mock,
	sampleId,
	targetStage,
}: {
	mock?: boolean;
	sampleId: string;
	targetStage:
		| "intent_classification"
		| "playbook_routing"
		| "tool_and_evidence"
		| "knowledge_retrieval"
		| "prompt_response"
		| "quality_guard";
}) {
	const params = new URLSearchParams({
		source: "p10_single_stage",
		sample_id: sampleId,
		target_stage: targetStage,
		candidate_mode: "manual",
	});
	if (mock) {
		params.set("mock", "1");
	}
	return `/eval?${params.toString()}#single-stage-candidate`;
}

function stageSummaryValue(result: StageResult, label: string) {
	return result.summary.find((item) => item.label === label)?.value ?? "未返回";
}

function DataModeHelp({ selectedModeId }: { selectedModeId: DataMode }) {
	const selectedMode = DATA_MODES.find((item) => item.id === selectedModeId);

	return (
		<div className="group relative z-20 inline-flex">
			<button
				type="button"
				data-testid="data-mode-help-trigger"
				aria-label="查看模式说明"
				aria-describedby="data-mode-help-panel"
				className="inline-flex items-center gap-1 rounded border border-transparent px-1 py-0.5 text-xs font-medium text-text-muted transition hover:border-surface-border hover:bg-surface-subtle hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/50"
			>
				<HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
				说明
			</button>
			<div
				id="data-mode-help-panel"
				role="tooltip"
				data-testid="data-mode-help-panel"
				className="invisible absolute right-0 top-7 w-[min(560px,calc(100vw-2rem))] rounded-md border border-surface-border bg-surface-card p-3 text-left text-xs text-text-secondary opacity-0 shadow-xl transition group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
			>
				<div className="mb-3 flex items-start justify-between gap-3 border-b border-surface-border pb-2">
					<div>
						<div className="font-semibold text-text-primary">四种数据模式怎么用</div>
						<p className="mt-1 leading-5">
							模式只决定本次测试证据口径，不改变主链路产品目标；验收时先看“能证明什么”和“不能证明什么”。
						</p>
					</div>
					<StatusPill tone="neutral">{`当前 ${selectedMode?.label ?? "未选"}`}</StatusPill>
				</div>
				<div className="grid gap-2">
					{DATA_MODES.map((item) => (
						<div
							key={item.id}
							className={`rounded-md border p-2 ${
								item.id === selectedModeId
									? "border-brand-accent bg-brand-accent/10"
									: "border-surface-border bg-surface-background"
							}`}
						>
							<div className="flex items-center justify-between gap-2">
								<div className="font-semibold text-text-primary">{item.label}</div>
								<StatusPill tone={dataModeTone(item.id)}>{item.state}</StatusPill>
							</div>
							<div className="mt-1 leading-5">{item.guidance}</div>
							<div className="mt-2 grid gap-1 leading-5 md:grid-cols-3">
								<div>
									<span className="font-medium text-text-primary">使用场景：</span>
									{item.useCase}
								</div>
								<div>
									<span className="font-medium text-text-primary">能证明：</span>
									{item.proves}
								</div>
								<div>
									<span className="font-medium text-text-primary">不能证明：</span>
									{item.doesNotProve}
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

function RecallInjectionAuditProjectionPanel({
	projection,
}: {
	projection: RecallInjectionAuditProjection;
}) {
	return (
		<div
			className="rounded-md bg-surface-subtle p-3"
			data-testid="recall-injection-audit-projection"
		>
			<div className="flex items-start justify-between gap-3">
				<div>
					<div className="flex items-center gap-2 font-medium text-text-primary">
						<ClipboardCheck className="h-4 w-4 text-brand-primary" aria-hidden="true" />
						选择 / 召回 / 注入审计
					</div>
					<p className="mt-1 text-xs leading-5 text-text-secondary">{projection.gapSummary}</p>
				</div>
				<StatusPill tone={auditProjectionTone(projection.overallStatus)}>
					{projection.coverageLabel}
				</StatusPill>
			</div>
			<div className="mt-3 grid gap-2">
				{projection.items.map((item) => (
					<RecallInjectionAuditProjectionRow key={item.key} item={item} />
				))}
			</div>
		</div>
	);
}

function RecallInjectionAuditProjectionRow({ item }: { item: RecallInjectionAuditItem }) {
	return (
		<div className="rounded border border-surface-border bg-surface-card px-2 py-2">
			<div className="flex items-start justify-between gap-2">
				<div>
					<div className="font-medium text-text-primary">{item.title}</div>
					<p className="mt-1 leading-6 text-text-secondary">{item.summary}</p>
				</div>
				<StatusPill tone={auditProjectionTone(item.status)}>{item.statusLabel}</StatusPill>
			</div>
			<div className="mt-2 flex flex-wrap gap-1.5">
				{item.fields.map((fieldValue) => (
					<span
						key={`${item.key}-${fieldValue}`}
						className="rounded border border-surface-border bg-surface-subtle px-1.5 py-1 font-mono text-[11px] leading-4 text-text-secondary"
					>
						{fieldValue}
					</span>
				))}
			</div>
			<div className="mt-1 text-[11px] leading-5 text-text-muted">来源：{item.source}</div>
		</div>
	);
}

function auditProjectionTone(status: AuditProjectionStatus): StepTone {
	if (status === "complete") return "success";
	if (status === "partial") return "warning";
	return "neutral";
}

function RunConclusion({
	mode,
	result,
	status,
	error,
	runEvents,
	totalLatencyMs,
}: {
	mode: DataMode;
	result: PipelineMessageResponse | null;
	status: RunStatus;
	error: string | null;
	runEvents: PipelineAdminRunEvent[];
	totalLatencyMs: number | null;
}) {
	const isRealLimited = mode === "real_write";
	const isRunning = status === "running";
	const isError = status === "error";
	const fallbackReply = "运行后展示最终回复。";
	const replyText = result?.reply_text ?? fallbackReply;
	const phase4 = result?.phase_trace.find((entry) => entry.phase === "phase4_generate");
	const evidenceRefs = Array.isArray(phase4?.summary.evidence_refs)
		? phase4?.summary.evidence_refs.map((item) => String(item))
		: [];
	const businessDecision = buildBusinessDecision(result, {
		isRunning,
		isError,
		isRealLimited,
	});
	const responseEffectItems = buildResponseEffectItems(result, businessDecision, evidenceRefs, {
		isRunning,
		isError,
	});
	const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
	const auditEvents = runEvents.filter(isAuditRunEvent);
	const recallInjectionProjection = buildRecallInjectionAuditProjection(result, runEvents);
	const runtimeDiagnostics = buildRuntimeDiagnosticItems(result);
	const totalLatencyLabel = totalLatencyMs !== null ? formatDurationMs(totalLatencyMs) : "待运行";
	const slowestPhase = slowestPhaseLabel(result);
	const phaseTotalMs =
		result?.phase_trace.reduce((total, entry) => total + Math.max(entry.elapsed_ms, 0), 0) ?? 0;

	return (
		<section
			className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]"
			data-testid="pipeline-run-conclusion"
		>
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">本次运行结论</CardTitle>
					<CardDescription>
						{isRunning
							? "正在调用真实主链路。"
							: isError
								? "真实主链路调用失败，请按错误信息定位。"
								: isRealLimited
									? "真实依赖仍受限，暂不计为真实端到端通过。"
									: "流程可读、风险可见；可作为本次回归证据。"}
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					{isError ? (
						<div className="rounded-md border border-brand-danger/30 bg-brand-danger/10 p-3 text-sm text-brand-danger">
							{error}
						</div>
					) : null}
					<div className="rounded-md border border-surface-border bg-surface-background p-4">
						<div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
							<FileText className="h-4 w-4 text-brand-primary" aria-hidden="true" />
							最终回复
						</div>
						<p className="mt-2 text-sm leading-6 text-text-secondary">
							{isRunning ? "运行中，请稍候。" : replyText}
						</p>
					</div>

					<ResponseEffectPanel items={responseEffectItems} />
					<BusinessDecisionPanel decision={businessDecision} />

					<div className="rounded-md border border-surface-border bg-surface-background p-4">
						<div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
							<div>
								<div className="text-sm font-semibold text-text-primary">技术诊断明细</div>
								<p className="mt-1 text-sm leading-6 text-text-secondary">
									默认不占用验收主线；排障时展开查看运行真实度、耗时和 Phase 明细。
								</p>
							</div>
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={() => setShowTechnicalDetails((value) => !value)}
							>
								{showTechnicalDetails ? "收起技术诊断明细" : "查看技术诊断明细"}
							</Button>
						</div>
						{showTechnicalDetails ? (
							<div className="mt-4 space-y-4" data-testid="technical-diagnostics-detail">
								{result ? (
									<RuntimeCapabilityPanel capabilities={result.runtime_capabilities} />
								) : null}
								{result ? <RuntimeDiagnosticsPanel items={runtimeDiagnostics} /> : null}
								<div className="grid gap-3 md:grid-cols-3">
									<ResultMetric label="总耗时" value={totalLatencyLabel} />
									<ResultMetric label="最慢阶段" value={slowestPhase} />
									<ResultMetric label="最终动作" value={result?.final_action_type ?? "待运行"} />
									<ResultMetric
										label="最终结果"
										value={result?.final_outcome ?? (isRealLimited ? "部分成功" : "待运行")}
									/>
									<ResultMetric label="测试建议" value="建议纳入测试集" />
								</div>
								{result ? (
									<div className="rounded-md border border-surface-border bg-surface-background p-4">
										<div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
											<div className="text-sm font-semibold text-text-primary">Phase 执行明细</div>
											<div className="text-xs text-text-muted">
												总耗时为服务端实测；Phase 为 orchestrator 内部实测。
											</div>
										</div>
										<div className="mt-3 grid gap-2">
											{result.phase_trace.map((entry) => {
												const percent =
													phaseTotalMs > 0
														? Math.max(4, Math.round((entry.elapsed_ms / phaseTotalMs) * 100))
														: 0;
												return (
													<div
														key={entry.phase}
														className="grid gap-2 rounded-md bg-surface-subtle p-3 text-sm md:grid-cols-[170px_72px_92px_120px_minmax(0,1fr)]"
													>
														<div className="font-mono text-text-primary">{entry.phase}</div>
														<div>{entry.status}</div>
														<div>{formatDurationMs(entry.elapsed_ms)}</div>
														<div className="flex items-center gap-2">
															<div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-border">
																<div
																	className="h-full rounded-full bg-brand-primary"
																	style={{ width: `${percent}%` }}
																/>
															</div>
															<span className="w-8 text-xs text-text-muted">{percent}%</span>
														</div>
														<div className="font-mono text-xs text-text-secondary">
															{summaryPreview(entry.summary)}
														</div>
													</div>
												);
											})}
										</div>
									</div>
								) : null}
							</div>
						) : null}
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle className="text-lg">证据与审计</CardTitle>
					<CardDescription>用于产品验收、QA 回归和研发定位。</CardDescription>
				</CardHeader>
				<CardContent className="space-y-3 text-sm">
					<div className="rounded-md bg-surface-subtle p-3">
						<div className="flex items-center gap-2 font-medium text-text-primary">
							<Database className="h-4 w-4 text-brand-primary" aria-hidden="true" />
							证据引用
						</div>
						<ul className="mt-2 space-y-1 text-text-secondary">
							{evidenceRefs.length > 0 ? (
								evidenceRefs.map((item) => <li key={item}>evidence_refs: {item}</li>)
							) : result ? (
								<li>本次没有返回证据引用</li>
							) : (
								<li>运行后展示真实证据引用</li>
							)}
						</ul>
					</div>
					<div className="rounded-md bg-surface-subtle p-3">
						<div className="flex items-center gap-2 font-medium text-text-primary">
							<ShieldAlert className="h-4 w-4 text-brand-warning" aria-hidden="true" />
							运行记录：{result?.trace_id ?? "trace-demo-p2-001"}
						</div>
						<p className="mt-2 leading-6 text-text-secondary">
							{result
								? `session=${result.session_id}；intent=${result.intent.intent}；route=${routingTargetLabel(result)}；match_mode=${result.match_mode}`
								: "已标记数据模式、依赖边界和受限阶段；真实订单与 Qdrant 接入后，需要重跑并替换本次证据。"}
						</p>
					</div>
					<RecallInjectionAuditProjectionPanel projection={recallInjectionProjection} />
					<div className="rounded-md bg-surface-subtle p-3">
						<div className="font-medium text-text-primary">审计事件</div>
						<div className="mt-2 space-y-2">
							{auditEvents.length > 0 ? (
								auditEvents.map((event) => (
									<div
										key={event.event_id}
										className="rounded border border-surface-border bg-surface-card px-2 py-2"
									>
										<div className="flex items-center justify-between gap-2">
											<div className="font-medium text-text-primary">{event.title}</div>
											<StatusPill tone={runEventStatus(event.status).tone}>
												{eventStatusLabel(event.status)}
											</StatusPill>
										</div>
										<p className="mt-1 leading-6 text-text-secondary">{event.summary}</p>
										<div className="mt-1 font-mono text-xs text-text-muted">
											{event.event_type} · {event.run_id}
										</div>
									</div>
								))
							) : (
								<div className="text-text-secondary">运行后展示 trace 持久化和本轮完成事件。</div>
							)}
						</div>
					</div>
					<div className="grid grid-cols-2 gap-2">
						<MaintLink label="意图分类" to="/intent" />
						<MaintLink label="处理方案" to="/playbook" />
						<MaintLink label="工具与动作" to="/skill-tool" />
						<MaintLink label="知识与证据" to="/knowledge" />
						<MaintLink label="提示词与话术" to="/prompt-q" />
						<MaintLink label="评测运营" to="/eval" />
						<MaintLink label="运行记录" to="/debug" />
					</div>
				</CardContent>
			</Card>
		</section>
	);
}

function ResponseEffectPanel({ items }: { items: ResponseEffectItem[] }) {
	const overallTone = items.some((item) => item.tone === "blocked")
		? "blocked"
		: items.some((item) => item.tone === "warning")
			? "warning"
			: items.every((item) => item.tone === "success")
				? "success"
				: "neutral";

	return (
		<div
			className="rounded-md border border-surface-border bg-surface-background p-4"
			data-testid="response-effect-panel"
		>
			<div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
				<div>
					<div className="text-sm font-semibold text-text-primary">应答效果判断</div>
					<p className="mt-1 text-sm leading-6 text-text-secondary">
						先看这四项判断本轮回复是否贴近目标；技术字段放到诊断明细和右侧 Inspector。
					</p>
				</div>
				<StatusPill tone={overallTone}>
					{overallTone === "blocked"
						? "需排障"
						: overallTone === "warning"
							? "需复核"
							: overallTone === "success"
								? "可验证"
								: "待判断"}
				</StatusPill>
			</div>
			<div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
				{items.map((item) => (
					<div
						key={item.label}
						className="rounded-md border border-surface-border bg-surface-subtle p-3"
					>
						<div className="flex items-center justify-between gap-2">
							<div className="text-xs font-medium text-text-muted">{item.label}</div>
							<StatusPill tone={item.tone}>{item.value}</StatusPill>
						</div>
						<p className="mt-2 text-sm leading-6 text-text-secondary">{item.summary}</p>
					</div>
				))}
			</div>
		</div>
	);
}

function buildResponseEffectItems(
	result: PipelineMessageResponse | null,
	decision: BusinessDecision,
	evidenceRefs: string[],
	flags: { isRunning: boolean; isError: boolean },
): ResponseEffectItem[] {
	if (flags.isRunning) {
		return [
			pendingEffectItem("任务理解", "等待主链路返回语义任务和意图分类。"),
			pendingEffectItem("证据支撑", "等待工具、知识或无需证据的判断结果。"),
			pendingEffectItem("回复推进", "等待最终回复和对客动作。"),
			pendingEffectItem("沉淀建议", "运行完成后再判断是否进入测试集或 badcase。"),
		];
	}
	if (flags.isError) {
		return [
			{
				label: "任务理解",
				value: "失败",
				summary: "主链路未返回稳定语义结果。",
				tone: "blocked",
			},
			{
				label: "证据支撑",
				value: "失败",
				summary: "证据失败或权限阻断，需要先排障。",
				tone: "blocked",
			},
			{
				label: "回复推进",
				value: "未生成",
				summary: "客户不可见回复未生成，不能作为应答效果样本。",
				tone: "blocked",
			},
			{
				label: "沉淀建议",
				value: "排障",
				summary: "先登记为运行故障，不进入正常效果对比。",
				tone: "blocked",
			},
		];
	}
	if (!result) {
		return [
			pendingEffectItem("任务理解", "运行后展示客户目标、意图分类和处理路径。"),
			pendingEffectItem("证据支撑", "运行后展示证据是否返回、是否被回复采用。"),
			pendingEffectItem("回复推进", "运行后判断回复是否回答、追问、执行或转人工。"),
			pendingEffectItem("沉淀建议", "运行后给出测试集、复跑或 badcase 候选建议。"),
		];
	}

	const taskLabel = semanticTaskLabel(decision.semanticTask);
	const evidenceTone =
		decision.evidenceStatus === "has_evidence"
			? "success"
			: decision.evidenceStatus === "evidence_failed_or_blocked"
				? "blocked"
				: result.final_action_type === "clarify"
					? "neutral"
					: "warning";
	const replyTone =
		result.final_outcome === "failed" || result.final_outcome === "handoff"
			? "blocked"
			: result.final_action_type === "clarify" || result.final_outcome === "partial"
				? "warning"
				: "success";
	const sedimentTone = decision.verificationScope.includes("阻塞")
		? "blocked"
		: decision.verificationScope.includes("追问")
			? "warning"
			: decision.verificationScope.includes("产品验证")
				? "success"
				: "neutral";
	const sedimentValue =
		sedimentTone === "success"
			? "可沉淀"
			: sedimentTone === "warning"
				? "追问样本"
				: sedimentTone === "blocked"
					? "先排障"
					: "观察";

	return [
		{
			label: "任务理解",
			value: taskLabel === "未返回" ? "待复核" : "可解释",
			summary: `${taskLabel}；意图分类 ${productIntentLabel(result.intent.intent)}，处理路径 ${routingTargetLabel(result)}。`,
			tone: taskLabel === "未返回" ? "warning" : "success",
		},
		{
			label: "证据支撑",
			value: decision.evidenceStatusLabel,
			summary:
				evidenceRefs.length > 0
					? `回复采用 ${evidenceRefs.length} 条证据，可继续核对事实是否准确。`
					: "本轮没有证据引用，需结合任务判断是否合理。",
			tone: evidenceTone,
		},
		{
			label: "回复推进",
			value: decision.customerVisibleAction,
			summary: `${decision.decisionBasisLabel}；下一步：${decision.nextAction}。`,
			tone: replyTone,
		},
		{
			label: "沉淀建议",
			value: sedimentValue,
			summary: decision.verificationScope,
			tone: sedimentTone,
		},
	];
}

function pendingEffectItem(label: string, summary: string): ResponseEffectItem {
	return {
		label,
		value: "待运行",
		summary,
		tone: "neutral",
	};
}

function BusinessDecisionPanel({ decision }: { decision: BusinessDecision }) {
	const missingSlots = decision.missingSlots.length > 0 ? decision.missingSlots.join(", ") : "无";

	return (
		<div
			className="rounded-md border border-surface-border bg-surface-background p-4"
			data-testid="business-decision-panel"
		>
			<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
				<div>
					<div className="text-sm font-semibold text-text-primary">业务判断</div>
					<p className="mt-1 text-sm leading-6 text-text-secondary">
						把语义任务、证据状态和对客动作翻译成产品验收能直接看的口径。
					</p>
				</div>
				<StatusPill tone={decision.tone}>{decision.verificationScope}</StatusPill>
			</div>
			<div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
				<ResultMetric label="服务任务" value={decision.semanticTask} />
				<ResultMetric label="判断依据" value={decision.decisionBasisLabel} />
				<ResultMetric label="对客动作" value={decision.customerVisibleAction} />
				<ResultMetric label="证据状态" value={decision.evidenceStatusLabel} />
			</div>
			<div className="mt-3 grid gap-3 md:grid-cols-3">
				<ResultMetric label="缺口信息" value={missingSlots} />
				<ResultMetric label="验收口径" value={decision.verificationScope} />
				<ResultMetric label="下一步" value={decision.nextAction} />
			</div>
		</div>
	);
}

function RuntimeDiagnosticsPanel({ items }: { items: RuntimeDiagnosticItem[] }) {
	return (
		<div
			className="rounded-md border border-surface-border bg-surface-background p-4"
			data-testid="runtime-diagnostics-panel"
		>
			<div className="text-sm font-semibold text-text-primary">运行判断字段</div>
			<p className="mt-1 text-sm leading-6 text-text-secondary">
				运行完成后才出现，用于定位主 Agent 回退、工具循环和质检判断。
			</p>
			<div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
				{items.map((item) => (
					<ResultMetric key={item.label} label={item.label} value={item.value} />
				))}
			</div>
		</div>
	);
}

function RuntimeCapabilityPanel({
	capabilities,
}: {
	capabilities: PipelineRuntimeCapabilities;
}) {
	const items = runtimeCapabilityItems(capabilities);

	return (
		<div
			className="rounded-md border border-surface-border bg-surface-background p-4"
			data-testid="runtime-capability-panel"
		>
			<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
				<div>
					<div className="text-sm font-semibold text-text-primary">本次运行真实度</div>
					<p className="mt-1 text-sm leading-6 text-text-secondary">
						用于判断本次结果能否作为产品验收。
					</p>
				</div>
				<StatusPill tone={runtimeCapabilityOverallTone(items)}>
					{runtimeCapabilityOverallLabel(items)}
				</StatusPill>
			</div>
			<div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
				{items.map((item) => (
					<div key={item.label} className="rounded-md bg-surface-subtle px-3 py-3">
						<div className="flex items-center justify-between gap-2">
							<div className="text-xs text-text-muted">{item.label}</div>
							<StatusPill tone={item.tone}>{item.value}</StatusPill>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

function ResultMetric({ label, value }: { label: string; value: string }) {
	return (
		<div className="rounded-md border border-surface-border bg-surface-subtle px-3 py-3">
			<div className="text-xs text-text-muted">{label}</div>
			<div className="mt-1 text-sm font-semibold text-text-primary">{value}</div>
		</div>
	);
}

function MaintLink({
	label,
	to,
}: {
	label: string;
	to: "/debug" | "/intent" | "/playbook" | "/skill-tool" | "/knowledge" | "/prompt-q" | "/eval";
}) {
	return (
		<Link
			to={to}
			className="inline-flex items-center justify-between rounded-md border border-surface-border bg-surface-background px-3 py-2 text-sm font-medium text-text-primary hover:bg-surface-subtle"
		>
			{label}
			<ArrowRight className="h-3.5 w-3.5 text-text-muted" aria-hidden="true" />
		</Link>
	);
}

function stageFromIntent(data: PipelineIntentResponse): StageResult {
	return {
		kind: "intent",
		title: "意图识别结果",
		status: "success",
		summary: [
			{ label: "意图", value: productIntentLabel(data.intent) },
			{ label: "置信度", value: formatPercent(data.confidence) },
			{ label: "实现层级", value: data.implementation_level },
			{ label: "规则", value: intentRuleLabel(data.l2_matched_rule_id) },
			{ label: "连续性", value: data.intent_continuity },
			{ label: "服务阶段", value: serviceStageLabel(data.service_stage) },
		],
		raw: data,
	};
}

function stageFromDispatch(data: PipelineDispatchResponse): StageResult {
	const routeLabel = routingTargetLabel(data);
	return {
		kind: "playbook",
		title: "处理路径匹配结果",
		status: data.recommended_playbook || data.short_circuit_skill ? "success" : "partial",
		summary: [
			{ label: "意图", value: productIntentLabel(data.intent.intent) },
			{ label: "处理路径", value: routeLabel },
			{ label: "方案名称", value: data.recommended_playbook?.name ?? "通用兜底" },
			{ label: "匹配模式", value: matchModeLabel(data.match_mode) },
			{ label: "规则 ID", value: data.routing_rule_id ?? "未返回" },
			{ label: "推荐工具", value: skillListLabel(data.recommended_skills) },
			{ label: "通用工具", value: skillListLabel(data.universal_skills) },
		],
		raw: data,
	};
}

function stageFromTools(
	oauth: PromiseSettledResult<unknown>,
	skill: PromiseSettledResult<unknown>,
	knowledge: PromiseSettledResult<unknown>,
): StageResult {
	const oauthText = settledText(oauth, (value) => {
		const item = value as {
			message?: string;
			shopify_api_status?: string;
			shopify_domain?: string;
			checked_live?: boolean;
		};
		return `${item.message ?? "OAuth health checked"}；${item.shopify_domain ?? "unknown shop"}；status=${item.shopify_api_status ?? "unknown"}；live=${item.checked_live ? "yes" : "no"}`;
	});
	const skillText = settledText(skill, (value) => {
		const item = value as {
			skill_id?: string;
			message?: string;
			executed?: boolean;
			dry_run_only?: boolean;
		};
		return `${skillLabel(item.skill_id)}；${item.message ?? "工具沙盒已检查"}；执行=${item.executed ? "是" : "否"}；保护性试跑=${item.dry_run_only ? "是" : "否"}`;
	});
	const knowledgeText = settledText(knowledge, (value) => {
		const item = value as { audit_id?: string; total?: number; source?: string };
		return `${item.audit_id ?? "no audit"}；命中 ${item.total ?? 0} 条；来源 ${knowledgeSourceLabel(item.source ?? "unknown")}`;
	});
	const failedCount = [oauth, skill, knowledge].filter((item) => item.status === "rejected").length;

	return {
		kind: "tools",
		title: "工具与知识沙盒结果",
		status: failedCount === 0 ? "success" : failedCount === 3 ? "error" : "partial",
		summary: [
			{ label: "Shopify/OAuth", value: oauthText },
			{ label: skillLabel("SK05"), value: skillText },
			{ label: "知识检索", value: knowledgeText },
			{ label: "失败项", value: String(failedCount) },
		],
		raw: {
			oauth: settledRaw(oauth),
			skill: settledRaw(skill),
			knowledge: settledRaw(knowledge),
		},
	};
}

function stageFromKnowledge(
	data: Awaited<ReturnType<typeof runPipelineKnowledgeSandbox>>,
): StageResult {
	return {
		kind: "knowledge",
		title: "知识与证据检索结果",
		status: data.total > 0 ? "success" : "partial",
		summary: [
			{
				label: "知识检索",
				value: `${data.audit_id ?? "no audit"}；命中 ${data.total} 条；来源 ${knowledgeSourceLabel(data.source)}`,
			},
			{ label: "来源", value: knowledgeSourceLabel(data.source) },
			{ label: "命中数", value: String(data.total) },
			{ label: "审计", value: data.audit_id ?? "未返回" },
			{ label: "Top 文档", value: data.items.at(0)?.title ?? "未命中" },
		],
		raw: data,
	};
}

function stageFromPromptResponse(data: PipelineAdminRunResponse): StageResult {
	const generationEvent = data.events.find((event) => event.event_type === "generation_completed");
	const phase4 = data.messages_response.phase_trace.find(
		(entry) => entry.phase === "phase4_generate",
	);
	const snapshot = asRecord(generationEvent?.raw.prompt_snapshot);
	const evidenceRefs = Array.isArray(generationEvent?.evidence_refs)
		? generationEvent.evidence_refs.map((item) => String(item))
		: Array.isArray(phase4?.summary.evidence_refs)
			? phase4.summary.evidence_refs.map((item) => String(item))
			: [];
	const snapshotFinalReply =
		snapshot?.final_reply == null ? "" : stringifyScalar(snapshot.final_reply);
	const finalReply =
		snapshotFinalReply || data.final_reply || data.messages_response.reply_text || "未返回";
	const promptVersion =
		snapshot?.prompt_version == null ? "未返回" : stringifyScalar(snapshot.prompt_version);
	const promptEffect =
		snapshot?.prompt_effect_mode == null
			? data.messages_response.runtime_capabilities.prompt_effect_mode
			: stringifyScalar(snapshot.prompt_effect_mode);
	const runnerMode =
		snapshot?.runner_mode == null
			? data.messages_response.runtime_capabilities.phase4_runner_mode
			: stringifyScalar(snapshot.runner_mode);

	return {
		kind: "prompt",
		title: "回复生成结果",
		status: generationEvent ? "success" : "partial",
		summary: [
			{ label: "目标环节", value: targetStageLabel("prompt_response") },
			{
				label: "生成事件",
				value: eventLabel(generationEvent?.event_type ?? "未返回 generation_completed"),
			},
			{ label: "提示词版本", value: promptVersion },
			{ label: "提示词状态", value: promptEffect },
			{ label: "生成模式", value: runnerMode },
			{ label: "证据引用", value: evidenceRefs.join(", ") || "未返回" },
			{ label: "最终回复", value: finalReply },
		],
		raw: {
			target_stage: "prompt_response",
			run_id: data.run_id,
			trace_id: data.trace_id,
			generation_event: generationEvent ?? null,
			phase4: phase4 ?? null,
			messages_response: data.messages_response,
		},
	};
}

function stageFromQualityGuard(data: PipelineAdminRunResponse): StageResult {
	const qualityEvent = data.events.find((event) => event.event_type === "quality_completed");
	const phase5 = data.messages_response.phase_trace.find(
		(entry) => entry.phase === "phase5_validate",
	);
	const eventOutput = asRecord(qualityEvent?.output);
	const rawSummary = asRecord(qualityEvent?.raw.summary) ?? asRecord(phase5?.summary);
	const decision =
		(eventOutput?.decision == null ? "" : stringifyScalar(eventOutput.decision)) ||
		(rawSummary?.decision == null ? "" : stringifyScalar(rawSummary.decision)) ||
		(phase5?.status == null ? "" : stringifyScalar(phase5.status)) ||
		"未返回";
	const blockReason =
		(rawSummary?.block_reason == null ? "" : stringifyScalar(rawSummary.block_reason)) ||
		(eventOutput?.block_reason == null ? "" : stringifyScalar(eventOutput.block_reason)) ||
		"无";
	const qualityMode =
		(rawSummary?.quality_mode == null ? "" : stringifyScalar(rawSummary.quality_mode)) ||
		data.messages_response.runtime_capabilities.quality_mode;

	return {
		kind: "quality",
		title: "质量策略结果",
		status: qualityEvent?.status === "blocked" ? "partial" : qualityEvent ? "success" : "partial",
		summary: [
			{ label: "目标环节", value: targetStageLabel("quality_guard") },
			{
				label: "质量事件",
				value: eventLabel(qualityEvent?.event_type ?? "未返回 quality_completed"),
			},
			{ label: "质量判定", value: qualityDecisionLabel(decision) },
			{ label: "质量模式", value: qualityMode },
			{ label: "阻断原因", value: blockReason },
			{
				label: "最终动作",
				value: productFinalActionLabel(
					data.final_action_type || data.messages_response.final_action_type,
				),
			},
			{ label: "最终结果", value: data.final_outcome || data.messages_response.final_outcome },
		],
		raw: {
			target_stage: "quality_guard",
			run_id: data.run_id,
			trace_id: data.trace_id,
			quality_event: qualityEvent ?? null,
			phase5: phase5 ?? null,
			messages_response: data.messages_response,
		},
	};
}

function settledText<T>(result: PromiseSettledResult<T>, formatter: (value: T) => string): string {
	if (result.status === "fulfilled") return formatter(result.value);
	return `失败：${errorMessage(result.reason)}`;
}

function settledRaw<T>(result: PromiseSettledResult<T>): unknown {
	if (result.status === "fulfilled") return result.value;
	return { error: errorMessage(result.reason) };
}

function stageRunningTitle(kind: StageKind): string {
	if (kind === "intent") return "意图识别运行中";
	if (kind === "playbook") return "处理路径匹配运行中";
	if (kind === "knowledge") return "知识检索运行中";
	if (kind === "prompt") return "回复生成运行中";
	if (kind === "quality") return "质量策略运行中";
	return "工具与知识沙盒运行中";
}

function errorMessage(error: unknown): string {
	if (isAxiosError(error)) {
		const detail = error.response?.data;
		if (typeof detail === "string") return detail;
		if (detail && typeof detail === "object" && "detail" in detail) {
			return String((detail as { detail: unknown }).detail);
		}
		return error.message;
	}
	if (error instanceof Error) return error.message;
	return String(error);
}

function formatPercent(value: number): string {
	return `${Math.round(value * 100)}%`;
}

function summaryNumber(value: unknown): number | null {
	if (typeof value === "number" && Number.isFinite(value)) return value;
	if (typeof value === "string" && value.trim() !== "") {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : null;
	}
	return null;
}

function summaryText(
	summary: Record<string, unknown> | null | undefined,
	key: string,
	fallback: string,
): string {
	if (!summary || !(key in summary)) return fallback;
	const value = summary[key];
	if (Array.isArray(value)) return value.map((item) => stringifyScalar(item)).join(", ");
	if (value === null || value === undefined) return fallback;
	return String(value);
}

function summaryStringList(summary: Record<string, unknown> | undefined, key: string): string[] {
	if (!summary || !(key in summary)) return [];
	const value = summary[key];
	if (Array.isArray(value)) {
		return value.map((item) => String(item)).filter((item) => item.length > 0);
	}
	if (typeof value === "string" && value.trim()) return [value.trim()];
	return [];
}

function buildBusinessDecision(
	result: PipelineMessageResponse | null,
	flags: { isRunning: boolean; isError: boolean; isRealLimited: boolean },
): BusinessDecision {
	if (flags.isRunning) {
		return {
			semanticTask: "待运行",
			decisionBasis: "running",
			decisionBasisLabel: "正在运行主链路",
			customerVisibleAction: "待生成",
			evidenceStatus: "pending",
			evidenceStatusLabel: "待返回证据",
			missingSlots: [],
			verificationScope: "待运行",
			nextAction: "等待运行完成",
			tone: "neutral",
		};
	}
	if (flags.isError) {
		return {
			semanticTask: "调用失败",
			decisionBasis: "runtime_error",
			decisionBasisLabel: "主链路调用失败",
			customerVisibleAction: "未生成",
			evidenceStatus: "evidence_failed_or_blocked",
			evidenceStatusLabel: "证据失败或权限阻断",
			missingSlots: [],
			verificationScope: "阻塞，需排障或人工策略",
			nextAction: "查看接口错误和依赖健康",
			tone: "blocked",
		};
	}
	if (!result) {
		return {
			semanticTask: "待运行",
			decisionBasis: "not_started",
			decisionBasisLabel: "尚未运行",
			customerVisibleAction: "待生成",
			evidenceStatus: "pending",
			evidenceStatusLabel: "待返回证据",
			missingSlots: [],
			verificationScope: "待运行",
			nextAction: "运行全链路",
			tone: "neutral",
		};
	}

	const phaseByName = new Map(result.phase_trace.map((entry) => [entry.phase, entry]));
	const phase2 = phaseByName.get("phase2_recall");
	const phase4 = phaseByName.get("phase4_generate");
	const evidenceRefs = summaryStringList(phase4?.summary, "evidence_refs");
	const semanticTask =
		summaryText(phase4?.summary, "semantic_task", "") ||
		summaryText(phase2?.summary, "semantic_task", "") ||
		result.intent.intent;
	const missingSlots =
		summaryStringList(phase4?.summary, "missing_slots").length > 0
			? summaryStringList(phase4?.summary, "missing_slots")
			: summaryStringList(phase2?.summary, "missing_slots");
	const evidenceStatus =
		summaryText(phase4?.summary, "evidence_status", "") ||
		deriveEvidenceStatus(result, evidenceRefs);
	const decisionBasis =
		summaryText(phase4?.summary, "decision_basis", "") ||
		deriveDecisionBasis(result, missingSlots, evidenceStatus);
	const customerVisibleAction =
		summaryText(phase4?.summary, "customer_visible_action", "") ||
		customerActionLabel(result.final_action_type);
	const verificationScope = verificationScopeFor(result, flags.isRealLimited, evidenceStatus);

	return {
		semanticTask,
		decisionBasis,
		decisionBasisLabel: decisionBasisLabel(decisionBasis),
		customerVisibleAction,
		evidenceStatus,
		evidenceStatusLabel: evidenceStatusLabel(evidenceStatus),
		missingSlots,
		verificationScope,
		nextAction: nextActionFor(result, missingSlots, evidenceStatus, decisionBasis),
		tone: businessTone(result, flags.isRealLimited, evidenceStatus),
	};
}

function deriveEvidenceStatus(result: PipelineMessageResponse, evidenceRefs: string[]): string {
	if (evidenceRefs.length > 0 && result.final_action_type === "inform") return "has_evidence";
	if (result.final_action_type === "handoff" || result.final_outcome === "failed") {
		return "evidence_failed_or_blocked";
	}
	if (evidenceRefs.length > 0) return "evidence_not_used";
	return "no_evidence_needed_or_available";
}

function deriveDecisionBasis(
	result: PipelineMessageResponse,
	missingSlots: string[],
	evidenceStatus: string,
): string {
	if (missingSlots.length > 0) return "missing_required_information";
	if (result.final_action_type === "handoff" || result.final_outcome === "failed") {
		return "handoff_or_evidence_failure";
	}
	if (evidenceStatus === "has_evidence") return "evidence_grounded";
	if (result.recommended_playbook) return "playbook_guided_clarification";
	return "general_clarification";
}

function decisionBasisLabel(value: string): string {
	const labels: Record<string, string> = {
		missing_required_information: "缺少关键信息，先追问",
		handoff_or_evidence_failure: "证据失败或需要人工",
		evidence_grounded: "已基于工具/知识证据回答",
		playbook_guided_clarification: "方案命中后先澄清",
		general_clarification: "无明确任务，先澄清",
		running: "正在运行主链路",
		runtime_error: "主链路调用失败",
		not_started: "尚未运行",
	};
	return labels[value] ?? value;
}

function evidenceStatusLabel(value: string): string {
	const labels: Record<string, string> = {
		has_evidence: "已使用证据",
		evidence_failed_or_blocked: "证据失败或权限阻断",
		no_evidence_needed_or_available: "未使用证据",
		evidence_not_used: "有返回但本轮未采用",
		pending: "待返回证据",
	};
	return labels[value] ?? value;
}

function runtimeCapabilityItems(
	capabilities: PipelineRuntimeCapabilities,
): RuntimeCapabilityItem[] {
	return [
		{ label: "主回复", ...phase4RunnerDisplay(capabilities.phase4_runner_mode) },
		{ label: "Prompt", ...promptEffectDisplay(capabilities.prompt_effect_mode) },
		{ label: "知识召回", ...ragDisplay(capabilities.rag_mode) },
		{ label: "会话", ...sessionDisplay(capabilities.session_mode) },
		{ label: "质检", ...qualityDisplay(capabilities.quality_mode) },
		{ label: "人工", ...handoffDisplay(capabilities.handoff_mode) },
		{ label: "Shopify 读", ...shopifyReadDisplay(capabilities.shopify_read_mode) },
		{ label: "Shopify 写", ...shopifyWriteDisplay(capabilities.shopify_write_mode) },
	];
}

function runtimeCapabilityOverallTone(items: RuntimeCapabilityItem[]): StepTone {
	if (items.some((item) => item.tone === "blocked")) return "blocked";
	if (items.some((item) => item.tone === "warning")) return "warning";
	if (items.some((item) => item.tone === "neutral")) return "neutral";
	return "success";
}

function runtimeCapabilityOverallLabel(items: RuntimeCapabilityItem[]): string {
	const tone = runtimeCapabilityOverallTone(items);
	if (tone === "blocked") return "存在阻断";
	if (tone === "warning") return "含 starter/占位";
	if (tone === "neutral") return "部分待确认";
	return "真实链路";
}

function phase4RunnerDisplay(
	mode: PipelineRuntimeCapabilities["phase4_runner_mode"],
): Pick<RuntimeCapabilityItem, "value" | "tone"> {
	if (mode === "sdk_loop") return { value: "SDK 工具循环", tone: "success" };
	if (mode === "sdk_loop_fallback") return { value: "SDK 回退", tone: "warning" };
	if (mode === "llm") return { value: "主 Agent LLM", tone: "success" };
	if (mode === "llm_fallback") return { value: "LLM 回退", tone: "warning" };
	if (mode === "custom") return { value: "自定义实现", tone: "neutral" };
	return { value: "静态模板", tone: "warning" };
}

function promptEffectDisplay(
	mode: PipelineRuntimeCapabilities["prompt_effect_mode"],
): Pick<RuntimeCapabilityItem, "value" | "tone"> {
	if (mode === "consumed_by_llm") return { value: "LLM 已消费", tone: "success" };
	if (mode === "preview_only") return { value: "仅预览", tone: "neutral" };
	if (mode === "unknown") return { value: "未知", tone: "warning" };
	return { value: "Prompt 已渲染未消费", tone: "warning" };
}

function ragDisplay(
	mode: PipelineRuntimeCapabilities["rag_mode"],
): Pick<RuntimeCapabilityItem, "value" | "tone"> {
	if (mode === "qdrant_live") return { value: "Qdrant 真实", tone: "success" };
	if (mode === "configured") return { value: "已配置", tone: "neutral" };
	if (mode === "error") return { value: "召回异常", tone: "blocked" };
	return { value: "知识召回空实现", tone: "warning" };
}

function sessionDisplay(
	mode: PipelineRuntimeCapabilities["session_mode"],
): Pick<RuntimeCapabilityItem, "value" | "tone"> {
	if (mode === "pg_session") return { value: "PG 持久会话", tone: "success" };
	if (mode === "redis_pg_session") return { value: "Redis+PG 会话", tone: "success" };
	return { value: "请求内会话", tone: "warning" };
}

function qualityDisplay(
	mode: PipelineRuntimeCapabilities["quality_mode"],
): Pick<RuntimeCapabilityItem, "value" | "tone"> {
	if (mode === "q_deep_side_effect") return { value: "深度质检已启用", tone: "success" };
	if (mode === "q_fast_grounding_side_effect") {
		return { value: "Q-fast + 副作用门", tone: "success" };
	}
	if (mode === "custom") return { value: "自定义质检", tone: "neutral" };
	return { value: "Q-fast starter", tone: "warning" };
}

function handoffDisplay(
	mode: PipelineRuntimeCapabilities["handoff_mode"],
): Pick<RuntimeCapabilityItem, "value" | "tone"> {
	if (mode === "ticket_created") return { value: "工单已创建", tone: "success" };
	if (mode === "agent_connected") return { value: "人工已接入", tone: "success" };
	if (mode === "contract_only") return { value: "合同预留", tone: "warning" };
	if (mode === "disabled") return { value: "禁用", tone: "neutral" };
	return { value: "人工占位", tone: "warning" };
}

function shopifyReadDisplay(
	mode: PipelineRuntimeCapabilities["shopify_read_mode"],
): Pick<RuntimeCapabilityItem, "value" | "tone"> {
	if (mode === "live_read") return { value: "真实读取", tone: "success" };
	if (mode === "failed_or_blocked") return { value: "失败/受限", tone: "blocked" };
	return { value: "未请求", tone: "neutral" };
}

function shopifyWriteDisplay(
	mode: PipelineRuntimeCapabilities["shopify_write_mode"],
): Pick<RuntimeCapabilityItem, "value" | "tone"> {
	if (mode === "live_confirmed") return { value: "真实写入确认", tone: "success" };
	if (mode === "dry_run") return { value: "Dry-run", tone: "warning" };
	if (mode === "blocked") return { value: "阻断", tone: "blocked" };
	return { value: "禁用", tone: "neutral" };
}

function customerActionLabel(value: PipelineMessageResponse["final_action_type"]): string {
	if (value === "inform") return "基于证据回答";
	if (value === "clarify") return "追问补充信息";
	if (value === "commit_action") return "执行受控动作";
	return "转人工或排障";
}

function verificationScopeFor(
	result: PipelineMessageResponse,
	isRealLimited: boolean,
	evidenceStatus: string,
): string {
	if (result.final_outcome === "failed" || result.final_outcome === "handoff") {
		return "阻塞，需排障或人工策略";
	}
	if (result.final_outcome === "success" && evidenceStatus === "has_evidence" && !isRealLimited) {
		return "可作为产品验证样本";
	}
	if (result.final_outcome === "partial" || result.final_action_type === "clarify") {
		return "可验证追问体验";
	}
	if (isRealLimited) return "可验证流程，需真实依赖复跑";
	return "可纳入回归观察";
}

function nextActionFor(
	result: PipelineMessageResponse,
	missingSlots: string[],
	evidenceStatus: string,
	decisionBasis: string,
): string {
	if (missingSlots.length > 0) return `补齐 ${missingSlots.join(", ")} 后重跑`;
	if (evidenceStatus === "has_evidence") return "检查回复是否引用正确事实";
	if (result.final_outcome === "failed" || result.final_outcome === "handoff") {
		return "查看工具、知识或权限日志";
	}
	if (decisionBasis === "general_clarification") return "沉淀通用澄清样本";
	return "纳入测试集持续回归";
}

function businessTone(
	result: PipelineMessageResponse,
	isRealLimited: boolean,
	evidenceStatus: string,
): StepTone {
	if (result.final_outcome === "failed" || result.final_outcome === "handoff") return "blocked";
	if (
		isRealLimited ||
		result.final_outcome === "partial" ||
		result.final_action_type === "clarify"
	) {
		return "warning";
	}
	if (result.final_outcome === "success" && evidenceStatus === "has_evidence") return "success";
	return "neutral";
}

function finalStepStatus(
	result: PipelineMessageResponse | null,
): Pick<PipelineStep, "status" | "tone"> {
	if (!result) return { status: "跳过", tone: "neutral" };
	if (result.final_outcome === "success") return { status: "通过", tone: "success" };
	if (result.final_outcome === "partial") return { status: "受限", tone: "warning" };
	return { status: "受限", tone: "blocked" };
}

function asRecord(value: unknown): Record<string, unknown> | null {
	if (value && typeof value === "object" && !Array.isArray(value)) {
		return value as Record<string, unknown>;
	}
	return null;
}

function summaryPreview(summary: Record<string, unknown>): string {
	return Object.entries(summary)
		.slice(0, 6)
		.map(([key, value]) => `${key}=${stringifyScalar(value)}`)
		.join("；");
}

function stringifyScalar(value: unknown): string {
	if (Array.isArray(value)) return value.map((item) => stringifyScalar(item)).join(",");
	if (value === null || value === undefined) return "无";
	if (typeof value === "object") return JSON.stringify(value);
	return String(value);
}

function truncateForMetric(value: unknown): string {
	const text = stringifyScalar(value);
	return text.length > 120 ? `${text.slice(0, 120)}...` : text;
}

function formatJson(value: unknown): string {
	return JSON.stringify(value, null, 2);
}
